import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener,
  OnDestroy, OnInit, ViewChild, signal, effect, computed, untracked,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, forkJoin, filter } from 'rxjs';
import { MsalService } from '@azure/msal-angular';

import { AgentWsService } from '../shared/services/agent-ws.service';
import { SessionListService, SessionSummary } from '../shared/services/session-list.service';
import { SuggestionService, Suggestion } from '../shared/services/suggestion.service';
import { AgentCatalogService } from '../shared/services/agent-catalog.service';
import { AbilitiesService } from '../shared/services/abilities.service';
import { CatalogAgent } from '../shared/models/agent-catalog.model';
import { AiRoleAgentService } from '../shared/services/ai-role-agent.service';
import { AuthenticationService } from '../shared/services/authentication.service';
import { HubSurfaces, MeService } from '../shared/services/me.service';
import { UsageService } from '../shared/services/usage.service';
import { StatsService } from '../shared/services/stats.service';
import { UsageMeterComponent } from '../usage-meter/usage-meter.component';
import { UsageRingComponent } from '../usage-meter/usage-ring.component';
import { UsageWarningComponent } from '../usage-meter/usage-warning.component';
import { TimeSavedComponent } from '../time-saved/time-saved.component';
import { AuditService } from '../shared/services/audit.service';
import { BreakpointService } from '../shared/services/breakpoint.service';
import { DrawerModule } from 'primeng/drawer';
import { ConversationTurn } from '../shared/services/feedback.service';
import { SendFeedbackDialogComponent } from '../shared/components/send-feedback-dialog/send-feedback-dialog.component';
import { ThemeService, ThemeMode } from '../shared/services/theme.service';
import { WhatsNewService } from '../shared/services/whats-new.service';
import { ConnectionsService } from '../shared/services/connections.service';
import { HubDestinationsService } from '../shared/services/hub-destinations.service';
import { GuidedTourService } from '../shared/services/guided-tour.service';
import { AGENT_HUB_TOUR } from '../shared/config/agent-hub-tour';
import { MarkdownPipe } from '../shared/pipes/markdown.pipe';
import { environment } from '../../environments/environment';

import { PCheckRendererComponent, PCheckAction } from '../features/pcheck/components/pcheck-renderer/pcheck-renderer.component';
import { ToolActivityPillsComponent } from '../shared/components/tool-activity-pills/tool-activity-pills.component';
import {
  PCheckChatMessage, ContactItem, ExecutiveItem, AddedGuest, ChiliPiperSlot,
  RescheduleDetails, CancelDetails, AddParticipantsDetails, MeetingListItem,
} from '../features/pcheck/models/pcheck.model';
import { MeetingSelectedEvent } from '../features/pcheck/components/meeting-picker/meeting-picker.component';

import { AiRoleRendererComponent } from '../features/airoleagent/components/ai-role-renderer/ai-role-renderer.component';
import { AiRoleAction, DownloadItem } from '../features/airoleagent/models/ai-role.model';

import { LicextRendererComponent } from '../features/licextagent/components/licext-renderer/licext-renderer.component';
import {
  FormData as LicextFormData, LicextAction, LicextFlowComplete,
} from '../features/licextagent/models/licext.model';

import { Artifact } from '../shared/models/artifact.model';
import { ArtifactService } from '../shared/services/artifact.service';
import { ArtifactActionService } from '../shared/services/artifact-action.service';
import { ToastService } from '../shared/services/toast.service';
import { RichCopyService } from '../shared/services/rich-copy.service';
import { ArtifactCardComponent } from '../shared/components/artifact-card/artifact-card.component';
import { ReportPanelComponent } from '../shared/components/report-panel/report-panel.component';
import { NeatDealContextComponent } from '../features/neatagent/components/neat-deal-context/neat-deal-context.component';

/** agent_ids the pcheck renderer is wired for. Base PCHECK and IMPACT_PCHECK
 *  share the same (agent-agnostic) renderer, model, and event contract. */
const PCHECK_AGENT_IDS = ['PCHECK_AGENT', 'IMPACT_PCHECK_AGENT'];

/** agent_id for the AI Role Transformation agent. */
const AI_ROLE_AGENT_IDS = ['AI_ROLE_TRANSFORM_AGENT'];

/** agent_id for the NEAT Deal Analyzer. */
const NEAT_AGENT_IDS = ['NEAT_AGENT'];

/** Accepted file extensions and size limit for uploads (shared across all agents).
 *  Spreadsheet exports (e.g. a Salesforce report .xlsx) run larger than text
 *  transcripts, hence the 300 KB cap. */

/** Sentinel appended by the agent when the booking + call-objective flow is complete.
 *  Stripped from displayed content; triggers automatic prep-doc generation. */
const PULSE_CHECK_COMPLETE_MARKER = '[PULSE_CHECK_COMPLETE]';

/**
 * Host chat message. Extends the shared pcheck message (all the card/picker
 * fields the PCheckRenderer reads) with the agenthome-only suggestion + renderer
 * hint fields. IMPACT_PCHECK turns populate the pcheck fields via the
 * pcheck_ui / booking_slots / booking_confirmed events.
 * AI_ROLE_TRANSFORM turns populate `downloads` via download_ready events.
 * SFAGENT turns populate `sfReportUrl` via sf_report / text (report_url) events.
 */
/** Client-minted turn id (see AgentHomeChatComponent._sendTurnFrame). 32 hex
 *  chars from the Web Crypto API; falls back to time + random where
 *  randomUUID is unavailable (non-secure contexts). Uniqueness per session
 *  is all that matters. */
function generateTurnId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID().replace(/-/g, '');
  } catch { /* fall through */ }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}${Math.random().toString(16).slice(2, 10)}`;
}

interface ChatMessage extends PCheckChatMessage {
  suggestions?: SuggestionChip[];
  /** Confirm-before-write: pending write actions proposed by the runtime
   *  (emitted via an `action_proposal` event). Rendered as a confirmation card. */
  actionProposal?: ActionProposal;
  /** Set once the user answers (or a newer proposal supersedes this one), so
   *  the card locks read-only and its buttons stop rendering. */
  actionProposalResolved?: 'confirmed' | 'rejected' | 'superseded';
  rendererHint?: string;
  downloads?: DownloadItem[];
  /** Presigned S3 URL of the generated SF account report. Set when the runtime emits sf_report or text with report_url. */
  sfReportUrl?: string;
  /** True when the agent has generated a full account summary (enables summary card + Open report button). */
  summaryAvailable?: boolean;
  /** Whether the summary card is collapsed. Defaults to false (expanded) once streaming finishes. */
  isCollapsed?: boolean;
  /** NEAT: this assistant turn carries the in-chat deal-context wizard (emitted
   *  via a `neat_ui` event). Rendered inline until the user submits/skips. */
  neatDealContext?: boolean;
  /** NEAT: set once the user resolves the wizard, so it stops rendering. */
  neatDealContextDone?: boolean;
  /**
   * Renderer metadata stamped at turn completion and persisted with the message.
   * Lets session reload replay the correct component without re-deriving it from
   * the session-level activeAgentId signal (which only reflects the last done event).
   */
  ui?: {
    component: 'pcheck' | 'airole' | 'summary' | 'plain' | 'licext';
    agentId?: string;
    props?: Record<string, unknown>;
  };
  /** LICEXTAGENT: inline extension wizard payload (emitted via `licext_form`). */
  licextForm?: LicextFormData;
  /** LICEXTAGENT: set once the user submits the wizard, so it stops rendering. */
  licextFormSubmitted?: boolean;
  /** LICEXTAGENT: set on submit completion (emitted via `licext_flow_complete`). */
  licextFlowComplete?: LicextFlowComplete;
  /** Per-user OAuth connectors: the runtime's `connection_required` event —
   *  a tool needed the caller's own third-party access and they haven't
   *  connected that service yet. Rendered as a card that opens the
   *  Connections modal over the thread. */
  connectionRequired?: { connector: string; message: string };
  /** Live "Thinking" block text (flag-gated `reasoning_delta`/`reasoning`
   *  frames, off by default server-side). EPHEMERAL BY DESIGN: these three
   *  fields exist only in the live view-model — the `history` resume handler
   *  never sets them and nothing in this component sends message state to the
   *  server — so reasoning is never persisted and never reappears on resume. */
  reasoning?: string;
  /** Whether the Thinking block is expanded. Auto-opens on the first
   *  reasoning frame; auto-collapses when the answer text starts streaming. */
  reasoningOpen?: boolean;
  /** One-shot latch: the block already auto-collapsed (first text_delta/text
   *  of the turn, or `done`), so later deltas never fight a manual re-expand. */
  reasoningAutoCollapsed?: boolean;
  /** The user pressed Stop while this reply was streaming. Whatever had
   *  arrived stays; a "Stopped" note marks it. The server persists the same
   *  partial text with a stopped marker, so a resume shows the same thing. */
  isStopped?: boolean;
}

interface SuggestionChip {
  agent_id: string;
  label?: string;
  reason?: string;
  type?: string;
  value?: string;
}

/** Confirm-before-write card payload (from the runtime's `action_proposal`
 *  event). confirmMessage/rejectMessage are sent back VERBATIM as the next
 *  user turn — the token inside them is transport, never shown or edited. */
interface ActionProposal {
  title: string;
  actions: { id: string; name: string; summary: string }[];
  confirmMessage: string;
  rejectMessage: string;
}

/** Confirm-before-write transport prefixes — must match the rendered studio
 *  chat handler (_ACTION_CONFIRM_PREFIX / _ACTION_REJECT_PREFIX). Used only to
 *  mask raw tokens in displayed/restored user bubbles. */
const ACTION_CONFIRM_PREFIX = '__WG_CONFIRM_ACTION__:';
const ACTION_REJECT_PREFIX = '__WG_REJECT_ACTION__:';
const ACTION_CONFIRM_DISPLAY = '✓ Confirm — go ahead and write it';
const ACTION_REJECT_DISPLAY = 'Cancel — don\'t write anything';

/** Replace a raw confirm/reject token with its human label (restored sessions
 *  persist the wire text; nobody should ever read a base64 blob in chat). */
function maskActionToken(content: string): string {
  const trimmed = (content ?? '').trim();
  if (trimmed.startsWith(ACTION_CONFIRM_PREFIX)) return ACTION_CONFIRM_DISPLAY;
  if (trimmed.startsWith(ACTION_REJECT_PREFIX)) return ACTION_REJECT_DISPLAY;
  return content;
}

interface SlashCommandItem {
  agentId: string;
  agentName: string;
  command: string;
  label: string;
  prompt: string;
  /** True for the "command only" headline entry — selecting it drops just the
   *  command into the box so the user can type their own message after it. */
  insertOnly: boolean;
  searchText: string;
}

/** Legacy supervisor commands replaced by the Agent Catalog UI. */
const HIDDEN_SLASH_COMMANDS = new Set(['/home', '/agents']);

/** The one hub built-in the menu advertises. Selecting it drops `/help` into
 *  the box like any catalog command; the hub answers with its full command
 *  list (built-ins + the agent shortcuts this caller can see). No agentId:
 *  it is not an explicit agent pick. */
const HELP_SLASH_ITEM: SlashCommandItem = {
  agentId: '',
  agentName: 'Agent Hub',
  command: '/help',
  label: 'Show every command available to you',
  prompt: '',
  insertOnly: true,
  searchText: 'agent hub /help help commands',
};

/** Canonical lookup form of a typed slash token: lowercase, "-" → "_".
 *
 *  SYNC ANCHOR — this is a TypeScript mirror of the canonical Python contract
 *  wg-agents-runtimes/shared/command_contract.py :: normalize_command. The hub
 *  routes "/<cmd> …" from the catalog row this token resolves to, so client and
 *  server MUST fold a token the same way. This repo has no unit-test runner, so
 *  nothing here fails CI on drift (the studio-ui mirror IS pinned by
 *  hub-command.spec.ts, and the Python copies by test_command_contract_pinned.py).
 *  If you change normalize_command, change this line too. */
function normalizeSlashCommand(token: string): string {
  return token.trim().toLowerCase().replace(/-/g, '_');
}

/** Minimum time (ms) a "running" tool pill stays visible before it may flip to
 *  done/error — so sub-second tools don't flash straight to "complete". */
const PILL_MIN_VISIBLE_MS = 550;

/** Supervisor-internal tools that must NOT surface as user-facing activity pills.
 *  `dispatch_to_agent` is deliberately NOT here: its "Routing to agent…" pill is
 *  the only signal the user gets during the multi-second routing + downstream
 *  warm-up gap, so hiding it read as a hang. */
const HIDDEN_ACTIVITY_TOOLS = new Set<string>([
  'update_session_metadata',
]);

/** How long a turn may stay in flight with no visible reply before the
 *  "Working…" hint escalates to the still-working message (cold agent starts
 *  routinely exceed this; without it the pause reads as a hang). */
const STILL_WORKING_AFTER_MS = 8000;

/** How long the message copy button stays on its tick before reverting. */
const COPY_TICK_MS = 1500;

/** Delay between re-sends of `action_kind: 'resume'` while the backend still
 *  reports turn_status: "running" (the previous turn hasn't finished yet). */
const RESUME_POLL_INTERVAL_MS = 5000;

/** Give up polling after this many attempts (~5 min at the interval above) —
 *  a genuinely stuck backend turn shouldn't poll forever. The user can still
 *  reopen the session later; the next resume starts a fresh poll budget. */
const RESUME_POLL_MAX_ATTEMPTS = 60;

/** Auto-scroll stays engaged only while the user is reading the tail of the
 *  thread — within this many px of the bottom. Further up = they scrolled back
 *  on purpose; streaming must not yank them down. */
const SCROLL_PIN_THRESHOLD_PX = 150;

function generateSessionId(): string {
  const arr = new Uint8Array(17);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

function relativeTime(unixSec: number): string {
  const diff = Math.floor((Date.now() - unixSec * 1000) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/** A labelled bucket of sessions for the Recents list. An empty label means
 *  "render the rows with no sub-header" (used for the ungrouped/"None" mode). */
interface SessionGroup {
  key: string;
  label: string;
  sessions: SessionSummary[];
}

@Component({
  selector: 'app-agenthome-chat',
  standalone: true,
  imports: [MarkdownPipe, PCheckRendererComponent, AiRoleRendererComponent, LicextRendererComponent, ToolActivityPillsComponent, ArtifactCardComponent, ReportPanelComponent, NeatDealContextComponent, NgTemplateOutlet, DrawerModule, RouterLink, RouterLinkActive, UsageMeterComponent, UsageRingComponent, UsageWarningComponent, TimeSavedComponent, SendFeedbackDialogComponent],
  providers: [AgentWsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './agenthome-chat.component.html',
  styleUrls: ['./agenthome-chat.component.scss'],
})
export class AgentHomeChatComponent implements OnInit, OnDestroy {
  @ViewChild('messageContainer') private msgContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('chatTextarea')     private chatTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('aiRoleFileInput')  private aiRoleFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('sessionSearchInput') private sessionSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('managerSearchInput') private managerSearchInput?: ElementRef<HTMLInputElement>;

  // user info
  userName  = signal<string>('');
  userEmail = signal<string>('');
  isAdmin   = signal<boolean>(false);
  isStudio  = signal<boolean>(false);
  // Dev Tools portal is super_admin-only (platform/config surfaces migrated
  // out of admin-ui). Mirrors isAdmin's load-order: /me.role wins once loaded;
  // falls back to the login claim only on /me outages.
  isDevTools = signal<boolean>(false);
  // Developer role only (no approver) — used to restrict the Abilities catalog
  // to developer/admin/super_admin, distinct from isStudio (which also admits approver).
  isDeveloper = signal<boolean>(false);
  // Dev-only chat affordances (Studio button, file upload). Driven by the build
  // environment: true for local/dev, false for the production build.
  readonly isDevEnv = !environment.production;
  // Runtime surface kill-switches from GET /agenthome/me (config-repo
  // flags.json → SSM). null until /me answers (or on an older backend) — the
  // build-time environment flags are the fallback, so behaviour degrades to
  // exactly the pre-runtime-flag posture.
  private readonly surfaces = signal<HubSurfaces | null>(null);
  readonly showDashboards = computed(() =>
    this.surfaces()?.dashboards ?? environment.showDashboards);
  // Read-only Abilities catalog entry. Browse-only + access-safe, gated by the
  // surface flag (env flag as fallback) AND restricted to developer/admin/
  // super_admin — not shown to plain end users.
  readonly showAbilities = computed(() =>
    (this.surfaces()?.abilities ?? environment.showAbilities) &&
    (this.isAdmin() || this.isDeveloper()));
  // Stats ("time saved") widget in the welcome strip + user menu.
  readonly showStats = computed(() =>
    this.surfaces()?.stats ?? environment.showStats);
  // Apps launcher tile — every interactive app (embedded iframe apps from
  // GET /agenthome/apps + built-ins like License Extensions). Shown when the
  // surface is enabled AND the caller can actually open at least one app
  // (server-counted apps_available on /agenthome/me) — a user with zero apps
  // never sees the tile, mirroring the Workspace grant gate.
  //
  // Fails CLOSED on an unknown count. This used to fail OPEN (`n === null ||
  // n > 0`), which meant the tile rendered for everyone from first paint —
  // before /me resolved, and permanently if /me failed — so users with no app
  // access saw a launcher into an empty portal. Studio / Admin / Dev Tools all
  // start false and appear only once an affirmative check passes; Apps now
  // matches. The cost is that the tile pops in a beat after the others during
  // the /me round trip, which is the same behaviour Workspace already has.
  private readonly appsAvailable = signal<number | null>(null);
  readonly showApps = computed(() => {
    const enabled = this.surfaces()?.apps ?? environment.showApps;
    if (!enabled) return false;
    return (this.appsAvailable() ?? 0) > 0;
  });
  // Caller's Data Hub data-access grants (from GET /agenthome/me), used to gate
  // the Workspace menu entry below.
  private readonly dataAccess = signal<Record<string, string>>({});
  private readonly dataAll = signal<boolean>(false);
  // Workspace menu entry (report library + data tables). Shown only when the
  // surface is enabled AND the caller has at least one data_access grant (or
  // data_all) — so a user with no grants never sees the tab. Per-section access
  // is then enforced inside the Workspace.
  readonly showWorkspace = computed(() =>
    (this.surfaces()?.workspace ?? environment.showDataHub)
    && (this.dataAll() || Object.keys(this.dataAccess()).length > 0),
  );
  userMenuOpen = signal<boolean>(false);
  feedbackOpen = signal<boolean>(false);

  /** Which view fills ah-main: 'chat' (default) or 'data' (agents-data tab). */
  readonly currentView = signal<'chat' | 'data'>('chat');

  get firstName(): string {
    // Handles "saurabh.bohra" (email-style) or "Saurabh Bohra" (display name)
    const part = this.userName().split(/[.\s_]+/)[0] || 'there';
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  }

  /** Welcome greetings safe for anyone, including first-time users.
   *  Templated on the user's first name — a mix of name-first and name-last
   *  phrasings for variety. */
  private readonly GREETINGS: ((name: string) => string)[] = [
    n => `Hi ${n}! How can I help today?`,
    n => `Hello ${n}! What's on your mind?`,
    n => `Hey ${n}! What can I do for you?`,
    n => `Hi ${n}! What would you like to work on today?`,
    n => `How can I help, ${n}?`,
    n => `What's on your mind, ${n}?`,
    n => `What can I do for you, ${n}?`,
  ];
  /** Extra greetings that only make sense once the user has prior sessions —
   *  never shown to a first-time user. */
  private readonly RETURNING_GREETINGS: ((name: string) => string)[] = [
    n => `Good to see you again, ${n}. How can I assist?`,
    n => `Welcome back, ${n}! What can I help with?`,
    n => `Welcome back, ${n}! What would you like to do today?`,
  ];
  /** Greeting shown on the welcome screen. A signal (not a getter) so it stays
   *  stable across change-detection; re-rolled on init and on each New chat. */
  readonly greeting = signal<string>('');

  private pickGreeting(): void {
    const hasHistory = (this.sessions()?.length ?? 0) > 0;
    const pool = hasHistory
      ? [...this.GREETINGS, ...this.RETURNING_GREETINGS]
      : this.GREETINGS;
    const template = pool[Math.floor(Math.random() * pool.length)];
    this.greeting.set(template(this.firstName));
  }

  /** First + last initial (e.g. saurabh.bohra → SB). */
  get userInitials(): string {
    const raw = this.userName().trim();
    if (!raw) return '?';
    const parts = raw.split(/[.\s_]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length >= 2) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0]?.[0] ?? '?').toUpperCase();
  }

  // sessions panel
  sessions            = signal<SessionSummary[]>([]);
  sessionsLoading     = signal(true);
  sessionSearch       = signal('');
  sidebarCollapsed    = signal(false);
  /** Mobile drawer for the sidebar (below md, the sidebar becomes a p-drawer). */
  drawerOpen          = signal(false);
  /** Effective collapse: user-toggled OR force-collapsed below md.
   *  The inline sidebar shows the 56px rail in both cases; expanding on
   *  mobile opens the p-drawer instead of widening the inline rail. */
  railMode = computed(() => this.sidebarCollapsed() || !this.bp.isDesktop());
  sessionsSectionExpanded = signal(true);
  groupBy             = signal<'none' | 'date'>('none');  // Recents grouping mode
  groupByMenuOpen     = signal(false);
  confirmDeleteId     = signal<string | null>(null);
  renameId            = signal<string | null>(null);  // session being renamed inline
  renameDraft         = signal<string>('');           // editable title text
  /** session_id → optimistic title awaiting server confirmation. Overlaid on
   *  refreshed lists so a just-renamed title isn't clobbered by the GSI
   *  read-after-write lag; an entry self-clears once the server returns it. */
  private _pendingRenames = new Map<string, string>();
  sessionSearchOpen   = signal(false);
  searchHighlightIdx  = signal(0);
  sessionsManagerOpen = signal(false);
  managerSearch       = signal('');
  selectedSessionIds  = signal<Set<string>>(new Set());
  bulkDeleteConfirm   = signal(false);

  // suggestions (welcome screen chips)
  suggestions     = signal<Suggestion[]>([]);

  // slash-command picker (type / in chat input)
  catalogAgents   = signal<CatalogAgent[]>([]);
  slashMenuOpen   = signal(false);
  slashQuery      = signal('');
  slashHighlightIdx = signal(0);

  // active chat
  sessionId       = signal<string | null>(null);
  messages        = signal<ChatMessage[]>([]);
  currentInput    = signal('');
  isInFlight      = signal(false);  // send-button gating

  /** Id of the turn whose frames the handler currently accepts. Minted per
   *  send by _sendTurnFrame and echoed by AGENTHOME on every frame of that
   *  turn; frames tagged with any other id are dropped (see _handleWsEvent).
   *  This is what makes Stop safe: a late delta from a stopped turn can
   *  never render into — or clear the in-flight state of — the next one.
   *  (The previous Stop button had no turn identity and no server-side
   *  cancel, and was removed for exactly that race; see onStop.) */
  private _activeTurnId: string | null = null;
  /** Turns the user stopped whose server-side close-out (`done` with
   *  stopped: true) hasn't arrived yet. Frames tagged with these ids are
   *  swallowed; the close-out itself just refreshes the session list (the
   *  partial reply was persisted) and drops the id. Bounded. */
  private readonly _stoppedTurnIds = new Set<string>();

  /** id of the agent message whose copy button is showing the tick, or null. */
  copiedMsgId     = signal<number | null>(null);

  /** True once the current turn has been in flight for STILL_WORKING_AFTER_MS
   *  with no visible reply text — escalates the composer's "Working…" hint.
   *  Armed by the isInFlight effect (constructor), cleared the moment reply
   *  text streams in, on turn end, and on destroy. */
  stillWorking    = signal(false);

  /** True while a resumed session's last turn is still processing server-side
   *  (turn_status: "running" on the resume `done` frame — the user sent a
   *  message and left before the agent replied). The composer shows a distinct
   *  hint instead of "Working…"/"Still working…" and the resume request is
   *  re-sent on a poll loop (_scheduleResumePoll) until the backend reports
   *  turn_status "done"/"error" or the poll gives up. */
  resumePending   = signal(false);

  /** Quota gate: true once the user has hit their END_USER token limit. Quotas are
   *  enforced PER-LEDGER and Hub chat only ever spends the END_USER ledger, so the
   *  composer must gate on that ledger's usage — NOT the combined total across all
   *  ledgers (where BUILDER/Studio usage could falsely pause Hub). The server also
   *  rejects an over-quota turn (metering reserve on {cost_center}#{username}); this
   *  blocks the send client-side so the user gets immediate, honest feedback. */
  readonly usageBlocked = computed(() => this.usageSvc.endUserLevel() === 'reached');

  /** Tooltip/label for the paused composer — names the END_USER window that's
   *  capped (daily vs monthly) instead of the old hardcoded "Monthly". */
  readonly usageBlockedLabel = computed(() =>
    `${this.usageSvc.endUserWorstScope()} limit reached — sending is paused`);

  /** True while the latest agent message is showing an interactive (not yet
   *  confirmed) booking/reschedule slot picker. The composer is disabled so the
   *  user picks a slot from the calendar instead of typing free-text dates. */
  readonly slotPickerActive = computed(() => {
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== 'agent') return false;
    const bookingOpen    = !!last.bookingSlots?.length && !last.bookingConfirmed;
    const rescheduleOpen = !!last.rescheduleSlots?.length && !last.rescheduleSlotsConfirmed;
    return bookingOpen || rescheduleOpen;
  });

  /** True while the latest agent message is showing an interactive (not yet
   *  selected) primary-contact picker for Pulse Check / Impact Pulse Check.
   *  The composer is disabled so the user can only pick a primary contact
   *  from the Salesforce contact list, not type a different one by hand —
   *  mirrors the contact-picker's own template guard in
   *  pcheck-renderer.component.html. */
  readonly contactPickerActive = computed(() => {
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== 'agent') return false;
    return !!last.contactsList?.length
      && !last.addParticipantsDetails
      && !last.multipleMeetings?.length
      && !last.bookingSlots?.length
      && !last.contactsSelected;
  });

  isConnecting    = signal(false);
  isUploading     = signal(false);  // file upload in progress
  pendingAttachment = signal<{ filename: string } | null>(null); // file uploaded, awaiting user message
  activeAgentId   = signal<string | null>(null);

  agentState      = signal<'HOME' | 'IN_AGENT_MODE'>('HOME');

  /** agent_id the PCheckRenderer is wired for (template guard). */
  readonly pcheckAgentIds = PCHECK_AGENT_IDS;

  /**
   * Renderer key for an agent — CATALOG-DRIVEN: the backend catalog's
   * `uiRenderer` field ('pcheck' | 'airole' | 'neat') wins, so renderer
   * routing survives agent renames without a UI change. The hardcoded id
   * lists remain only as fallback for catalogs seeded before the field
   * existed (do NOT add new agents to them — set ui_renderer in
   * wg-agents-runtimes seed_catalog.py instead).
   */
  private rendererFor(id: string | null): string {
    if (!id) return '';
    const fromCatalog = this.catalogAgents().find(a => a.agentId === id)?.uiRenderer;
    if (fromCatalog) return fromCatalog;
    if (PCHECK_AGENT_IDS.includes(id)) return 'pcheck';
    if (AI_ROLE_AGENT_IDS.includes(id)) return 'airole';
    if (NEAT_AGENT_IDS.includes(id)) return 'neat';
    return '';
  }

  /** True when the given agent_id is rendered by the shared pcheck renderer. */
  isPcheckAgent(id: string | null): boolean {
    return this.rendererFor(id) === 'pcheck';
  }

  /** True when the active agent uses the AI-Role renderer — shows the upload button. */
  isAiRoleAgent(id: string | null): boolean {
    return this.rendererFor(id) === 'airole';
  }

  /**
   * Returns the renderer key for a message. Prefers the per-message `ui.component`
   * stamp (set at turn completion and persisted to DynamoDB) so that history replay
   * uses the correct component even when activeAgentId reflects a later agent or is null.
   * Falls back to the session-level signal for messages that predate this field.
   */
  _msgRenderer(msg: ChatMessage): string {
    if (msg.ui?.component) return msg.ui.component;
    const renderer = this.rendererFor(this.activeAgentId());
    if (renderer === 'pcheck' || renderer === 'airole') return renderer;
    if (msg.summaryAvailable) return 'summary';
    return 'plain';
  }

  /** True when the active agent uses the NEAT renderer — shows the upload button. */
  isNeatAgent(id: string | null): boolean {
    return this.rendererFor(id) === 'neat';
  }

  /**
   * Text to render in the chat bubble for a message, honouring its artifact's
   * `inlineContent` mode. For 'preamble' (SF summary) it keeps only the
   * conversational lead-in — everything before the first markdown heading/table/
   * rule, or the first paragraph if there's none — so the report body itself
   * shows only in the side panel, not as plain text in the chat.
   */
  chatBody(msg: ChatMessage): string {
    const content = msg.content ?? '';
    // Once the artifact is attached its inlineContent is authoritative. Before
    // that (mid-stream, the moment `summary_available` arrives on the text_delta)
    // a summary still trims to its lead-in so the report body never flashes in
    // the chat. Non-summary messages keep the full text.
    const mode = msg.artifact?.inlineContent ?? (msg.summaryAvailable ? 'preamble' : 'full');
    if (mode === 'none') return '';
    if (mode === 'preamble') {
      const headingIdx = content.search(/\n\s*(#{1,6}\s|\||-{3,}|\*{3,}|_{3,})/);
      const lead = headingIdx >= 0 ? content.slice(0, headingIdx) : content.split(/\n\s*\n/)[0];
      return lead.trim();
    }
    return content;
  }

  /**
   * True when a message should show its copy button. The latest message is
   * excluded while its turn is in flight, since `content` is only partially
   * appended by text_delta until the final `text` chunk lands.
   */
  canCopy(msg: ChatMessage, isLast: boolean): boolean {
    if (isLast && this.isInFlight()) return false;
    return msg.role === 'agent'
      && !msg.isThinking
      && !!(msg.content ?? '').trim();
  }

  /**
   * Copies the reply and flips the icon to a tick for a moment. Uses the full
   * `content`, not `chatBody()` — for report messages the latter is trimmed to
   * its lead-in, and the user expects the whole answer. Copied as rich text +
   * markdown so pasting into Teams/Outlook keeps the formatting instead of
   * showing literal `**bold**` and `-` bullets.
   */
  async copyMessage(msg: ChatMessage): Promise<void> {
    const text = (msg.content ?? '').trim();
    if (!text) return;
    try {
      await this.richCopy.copyMarkdown(text);
      clearTimeout(this._copyResetTimer);
      this.copiedMsgId.set(msg.id);
      this._copyResetTimer = setTimeout(() => this.copiedMsgId.set(null), COPY_TICK_MS);
    } catch {
      this.toast.error('Could not copy to clipboard');
    }
  }

  /**
   * Derive a report-panel Artifact from a completed/restored message, from the
   * per-agent fields the runtime already sends + persists. Returns undefined for
   * messages that produced no report. NEAT does NOT go through here — it sends a
   * dedicated `artifact` WS event handled in _handleWsEvent.
   *
   * The id is stable within a page session (sessionId:msgId) — enough for the
   * panel to track the active artifact and for the card to toggle it.
   */
  private _makeArtifact(m: ChatMessage, summaryAvailable: boolean): Artifact | undefined {
    const sid = this.sessionId() ?? 's';
    if (summaryAvailable) {
      // Prefer the styled HTML report (iframe) so the panel looks exactly like
      // opening the report link; fall back to the markdown body if there's no URL.
      return {
        id: `${sid}:${m.id}`, type: m.sfReportUrl ? 'document' : 'markdown',
        title: 'Account Summary', icon: 'pi-sparkles', agentId: 'SF_AGENT',
        body: m.content, fileUrl: m.sfReportUrl, fileName: 'account-summary.html',
        // The SF message text IS the full report. Always keep only the lead-in in
        // chat — the formatted report renders in the panel (styled iframe when a
        // URL exists, the markdown body otherwise). This also stops a resumed /
        // no-URL summary from replaying the full report as raw text in the chat.
        inlineContent: 'preamble',
      };
    }
    if (m.reportUrl) {   // IMPACT/base PCHECK prep doc
      return {
        id: `${sid}:${m.id}`, type: 'document',
        title: 'Prep Doc', icon: 'pi-file', agentId: this.activeAgentId() ?? 'PCHECK_AGENT',
        body: m.content, fileUrl: m.reportUrl, fileName: 'prep-doc',
      };
    }
    return undefined;
  }

  /**
   * Surface a summary's report the moment it streams in (the `text_delta`/`text`
   * already carries `summary_available` + `report_url`) rather than waiting for
   * `done`: attach the artifact card to the current message and switch the panel
   * to it. Idempotent per message (skips once an artifact exists) and never runs
   * during a resume replay. Keeps the chat from flashing the raw report body and
   * makes the card + panel appear without the end-of-turn lag.
   */
  private _surfaceSummaryArtifact(): void {
    if (this._isResumingSession) return;
    const idx = this._currentAgentMsgIdx;
    const m = idx >= 0 ? this.messages()[idx] : undefined;
    if (!m || !m.summaryAvailable || m.artifact) return;
    const artifact = this._makeArtifact(m, true);
    if (!artifact) return;
    this._updateAgentMsg(mm => ({ ...mm, artifact }));
    this.artifacts.open(artifact);
  }

  /** Pre-generated session ID for welcome-screen uploads. Reused by the first
   *  onSendMessage() call so the file and the chat share the same session. */
  private _pendingUploadSessionId: string | null = null;

  private _msgId = 0;
  private _currentAgentMsgIdx = -1;
  private _selectedContactEmail = '';
  /** Running list of additional guests added in the current booking flow. The
   *  guest wizard re-renders empty each time the agent re-opens it (new message),
   *  so we track the confirmed list here and feed it back as the wizard's
   *  initialGuests — the user appends/removes instead of losing prior picks.
   *  Cleared on 'booking_confirmed' (see below) so it never leaks into the
   *  NEXT meeting booked in the same chat session. */
  private _additionalGuests: AddedGuest[] = [];
  /** Guards the one-shot prep-doc auto-send so it fires at most once per session. */
  private _prepDocAutoTriggered = false;
  /** True when the last send was a chip-driven /switch — the original user
   *  intent was never forwarded, so _silentTrigger must replay it. */
  private _pendingSilentTrigger = false;
  /** agent_id the user EXPLICITLY picked this session (catalog / slash-menu /
   *  suggestion chip). One-shot: the next ordinary (non-slash) chat send
   *  includes it as `selected_agent_id` — the server's direct-dispatch fast
   *  path then skips the ~2.5s router LLM pass — IF it still matches the
   *  session's active agent, and the flag clears either way. Never set for
   *  typed messages, so free-text re-routing ("switch to X") always keeps the
   *  supervisor router. Cleared on session switch / new chat. */
  private _pendingExplicitAgentPick: string | null = null;
  /** Timer behind the stillWorking escalation; managed by the isInFlight
   *  effect and cleared on destroy so it never outlives the component. */
  private _stillWorkingTimer: ReturnType<typeof setTimeout> | undefined;
  /** Timer that resets the copy button back from its tick; cleared on destroy. */
  private _copyResetTimer: ReturnType<typeof setTimeout> | undefined;
  /** A widget action the user triggered while the turn was still streaming
   *  (e.g. picked a slot before the `done` chunk arrived). Held here and flushed
   *  once `done` fires so the selection is never dropped. */
  private _pendingActionText: string | null = null;

  private wsSub?: Subscription;
  private stateSub?: Subscription;
  private resumeSub?: Subscription;
  private catalogSub?: Subscription;
  private userNameSub?: Subscription;
  private artifactActionSub?: Subscription;
  private navSub?: Subscription;
  private isDestroying = false;
  /** True while a resume turn is in-flight; suppresses the "Welcome back" greeting. */
  private _isResumingSession = false;
  /** Timer driving the resume poll loop (see _scheduleResumePoll); cleared on
   *  destroy and on every fresh resume so a stale poll can never fire into a
   *  session the user has since navigated away from. */
  private _resumePollTimer: ReturnType<typeof setTimeout> | undefined;
  /** Attempts made so far polling an in-flight resume; reset at the start of
   *  every resume. Bounds RESUME_POLL_MAX_ATTEMPTS so an abandoned turn that
   *  never finishes server-side (a genuine hang, not just slow) can't poll
   *  forever. */
  private _resumePollAttempts = 0;
  /** Whether the current socket attempt has actually reached 'connected'. Reset
   *  to false at the start of every _connectWs so the intentional teardown
   *  'disconnected' (emitted while opening a new socket) isn't mistaken for a
   *  mid-turn drop that would reset isInFlight and detach the reply placeholder. */
  private _socketConnected = false;

  constructor(
    private readonly wsService: AgentWsService,
    private readonly sessionListSvc: SessionListService,
    private readonly suggestionSvc: SuggestionService,
    private readonly catalogSvc: AgentCatalogService,
    private readonly aiRoleSvc: AiRoleAgentService,
    private readonly auth: AuthenticationService,
    private readonly meSvc: MeService,
    private readonly usageSvc: UsageService,
    private readonly statsSvc: StatsService,
    private readonly audit: AuditService,
    private readonly router: Router,
    private readonly msal: MsalService,
    readonly theme: ThemeService,
    readonly artifacts: ArtifactService,
    private readonly artifactActions: ArtifactActionService,
    private readonly toast: ToastService,
    private readonly richCopy: RichCopyService,
    private readonly abilitiesSvc: AbilitiesService,
    readonly whatsNew: WhatsNewService,
    private readonly tourSvc: GuidedTourService,
    private readonly hubDest: HubDestinationsService,
    readonly connections: ConnectionsService,
    readonly bp: BreakpointService,
  ) {
    // When the report panel opens (first open or reopen), collapse the left nav
    // so chat + report share the row evenly — the Claude-artifacts split view.
    // Guarded on the false→true transition so the user can still expand the nav
    // afterwards without it snapping shut again.
    effect(() => {
      const open = this.artifacts.panelOpen();
      if (open && !this._prevPanelOpen) this.sidebarCollapsed.set(true);
      if (!open) this.panelWidth.set(null);
      this._prevPanelOpen = open;
    });
    // First-run onboarding waits for /me to resolve the gated nav entries
    // (Abilities, Switch app) so the tour doesn't drop those steps. ngOnInit
    // arms a fallback timer for the /me-error case, where surfaces stays null.
    effect(() => {
      if (this.surfaces() !== null) this._runOnboardingOnce();
    });
    // Deferred tour starts (first run, or What's New → "Take the quick tour"
    // from any page): the anchors live in this component's sidebar, so expand
    // it, let it render, then hand control back to the tour engine.
    effect(() => {
      if (!this.tourSvc.pendingStart()) return;
      this.sidebarCollapsed.set(false);
      setTimeout(() => this.tourSvc.startPending(), 350);
    });
    // When a tour ends (finished or skipped), give What's New its once-per-user
    // auto-pop — announcements are shown after the walkthrough, not swallowed
    // by it. No-ops when everything is already seen.
    effect(() => {
      const active = this.tourSvc.active();
      if (this._tourWasActive && !active) {
        setTimeout(() => this.whatsNew.refreshThenMaybeAutoOpen(), 700);
      }
      this._tourWasActive = active;
    });
    // "Still working" escalation: every in-flight transition (re)arms a one-shot
    // timer; if the turn is STILL in flight after STILL_WORKING_AFTER_MS and the
    // reply bubble has no text yet (routing + a cold agent start), the composer
    // hint escalates so the wait doesn't read as a hang. Turn end (isInFlight →
    // false) cancels the timer and resets the hint; streamed text also clears it
    // (see the text_delta/text handlers).
    effect(() => {
      const inFlight = this.isInFlight();
      untracked(() => {
        clearTimeout(this._stillWorkingTimer);
        this._stillWorkingTimer = undefined;
        this.stillWorking.set(false);
        if (!inFlight) return;
        this._stillWorkingTimer = setTimeout(() => {
          const idx = this._currentAgentMsgIdx;
          const msg = idx >= 0 ? this.messages()[idx] : undefined;
          if (this.isInFlight() && !msg?.content) this.stillWorking.set(true);
        }, STILL_WORKING_AFTER_MS);
      });
    });
  }

  /** Previous tour-active state for the tour-end transition effect. */
  private _tourWasActive = false;

  /** Tracks the previous panelOpen() value so the auto-collapse effect only
   *  fires on the open transition, not on every change-detection pass. */
  private _prevPanelOpen = false;

  // ── Report panel resize ───────────────────────────────────────
  /** User-set panel width in px. null = use flex default (50/50 split). */
  panelWidth = signal<number | null>(null);

  private _resizeStartX = 0;
  private _resizeStartWidth = 0;

  onPanelResizeStart(event: PointerEvent): void {
    const handle = event.currentTarget as HTMLElement;
    const panel = handle.nextElementSibling as HTMLElement | null;
    if (!panel || event.button !== 0) return;
    handle.setPointerCapture(event.pointerId);
    this._resizeStartX = event.clientX;
    this._resizeStartWidth = panel.getBoundingClientRect().width;
    document.body.style.userSelect = 'none';
    event.preventDefault();
  }

  onPanelResizeMove(event: PointerEvent): void {
    if (!event.currentTarget || !(event.currentTarget as HTMLElement).hasPointerCapture(event.pointerId)) return;
    const dx = this._resizeStartX - event.clientX;
    const newWidth = Math.max(280, Math.min(this._resizeStartWidth + dx, window.innerWidth * 0.75));
    this.panelWidth.set(newWidth);
  }

  onPanelResizeEnd(): void {
    document.body.style.userSelect = '';
  }

  /** Theme switcher options for the profile-menu segmented control. */
  readonly themeOptions: { mode: ThemeMode; icon: string; label: string }[] = [
    { mode: 'light',  icon: 'pi pi-sun',     label: 'Light' },
    { mode: 'dark',   icon: 'pi pi-moon',    label: 'Dark' },
    { mode: 'system', icon: 'pi pi-desktop', label: 'System' },
  ];

  setTheme(mode: ThemeMode): void {
    this.theme.setMode(mode);
  }

  /** Footer quick-toggle: flip between light and dark (mirrors the admin
   * portal's footer theme button). The 3-way switch (incl. System) still lives
   * in the collapsed-rail menu. */
  toggleTheme(): void {
    this.theme.setMode(this.theme.isDark() ? 'light' : 'dark');
  }

  private _viewFromUrl(url: string): 'chat' | 'data' {
    return url.startsWith('/agents-data') ? 'data' : 'chat';
  }

  ngOnInit(): void {
    // Sync currentView with the URL on first load and on every navigation.
    // ChatReuseStrategy keeps this component alive across /chat ↔ /agents-data
    // transitions; NavigationEnd carries the new URL so we can switch the view.
    this.currentView.set(this._viewFromUrl(this.router.url));
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.currentView.set(this._viewFromUrl(e.urlAfterRedirects));
        // Any nav closes the mobile drawer so the user isn't left with an
        // overlay obscuring the routed content.
        if (this.drawerOpen()) this.drawerOpen.set(false);
      });

    this.userName.set(this.auth.currentUserName$.value ?? '');
    this.pickGreeting();   // after userName is set so the greeting includes the name

    // On phone, default the artifact/report panel closed so the chat isn't
    // overlaid on first paint. User can still open it via the panel trigger.
    if (this.bp.isPhone() && this.artifacts.panelOpen()) {
      this.artifacts.panelOpen.set(false);
    }

    // One-time probe that gates the Connections nav entry (empty catalog =
    // feature dark in this env; the probe never errors user-visibly).
    this.connections.ensureProbed();

    // Interactive artifacts (e.g. a dashboard's drill-down / refresh) ask the
    // agent to re-run via this channel; send it as a normal turn, or queue it if
    // a turn is already in flight (flushed on `done`, like other widget actions).
    this.artifactActionSub = this.artifactActions.messages$.subscribe(text => {
      const sid = this.sessionId();
      if (!sid || !text) return;
      if (this.isInFlight()) {
        this._pendingActionText = text;
        return;
      }
      this._sendMessage(sid, text);
    });
    this.userEmail.set(sessionStorage.getItem('current-email') ?? '');
    this.usageSvc.refresh();  // load token usage for the sidebar "my usage" widget
    this.statsSvc.refresh();  // load sessions/time-saved for the welcome strip + user menu
    // Onboarding fallback: if /me never answers (outage), still decide the
    // first-run experience from whatever nav is on screen after a grace period.
    setTimeout(() => this._runOnboardingOnce(), 4000);
    // `role` here is the login/Cognito claim — used only for the Studio
    // 'developer' case (developer is not a USER_REGISTRY role). The admin
    // decision is authoritative from GET /agenthome/me (USER_REGISTRY role),
    // so a DB demotion hides the button without re-login. Start hidden and
    // reveal only once the backend confirms admin (avoids flashing the Admin
    // Portal button to a demoted user).
    const role = (sessionStorage.getItem('current-role') ?? '').toLowerCase();
    this.isAdmin.set(false);
    // Studio access is ROLE-based (developer/approver/admin/super_admin) per the
    // platform role model — NOT gated on the build env (it must work on prod too).
    this.isStudio.set(role === 'developer' || role === 'approver');
    this.isDeveloper.set(role === 'developer');
    this.meSvc.getMe().subscribe({
      next: me => {
        // A deactivated/removed account (status != active) gets NO privileged
        // surfaces, regardless of any stale login claim — covers the already-open
        // tab (the backend also returns is_admin=false/role="" for these, but
        // gate explicitly so a stale sessionStorage claim can't leak a button).
        const deactivated = me.user_status !== 'active';
        // Refresh the frozen 'current-role' (captured once at login) with the
        // LIVE USER_REGISTRY role so a plain page revisit/refresh picks up a DB
        // role change, and the Studio/Admin handoffs (openStudioPortal/
        // openAdminPortal read sessionStorage 'current-role') forward it fresh.
        if (me.role) {
          sessionStorage.setItem('current-role', me.role);
        } else if (deactivated) {
          // No live role for a deactivated account — drop the stale one so the
          // handoffs can't forward a privileged role from an open tab.
          sessionStorage.removeItem('current-role');
        }
        const effectiveRole = me.role || role;
        this.isAdmin.set(!deactivated && me.is_admin);
        this.isStudio.set(
          !deactivated && (me.is_admin || effectiveRole === 'developer' || effectiveRole === 'approver'),
        );
        this.isDeveloper.set(!deactivated && effectiveRole === 'developer');
        this.isDevTools.set(!deactivated && effectiveRole === 'super_admin');
        // Workspace menu visibility follows the caller's resolved data_access
        // grants (a deactivated account gets nothing).
        this.dataAll.set(!deactivated && !!me.data_all);
        this.dataAccess.set(deactivated ? {} : (me.data_access ?? {}));
        // Runtime surface kill-switches (null on an older backend → env fallback).
        this.surfaces.set(deactivated
          ? { workspace: false, apps: false, dashboards: false, abilities: false, stats: false }
          : me.surfaces);
        // Server-side app count — hides the Apps entry when there is nothing
        // behind it (null = unknown → the entry follows the surface flag).
        this.appsAvailable.set(deactivated ? 0 : me.apps_available);
      },
      error: () => {
        // Backend unreachable — fall back to the login claim so a real admin
        // keeps their button during an outage (access is still server-enforced).
        this.isAdmin.set(role === 'admin' || role === 'super_admin');
        this.isStudio.set(role === 'admin' || role === 'super_admin' || role === 'developer' || role === 'approver');
        this.isDeveloper.set(role === 'developer');
        this.isDevTools.set(role === 'super_admin');
      },
    });
    this.userNameSub = this.auth.currentUserName$.subscribe(u => {
      this.userName.set(u ?? '');
      this.userEmail.set(sessionStorage.getItem('current-email') ?? '');
    });
    this._loadSessions();
    this._loadSuggestions();
    this.catalogSvc.getCatalog().subscribe(agents => {
      this.catalogAgents.set(
        // Exclude embedded apps (outputType === 'app', e.g. Account Review, Deal
        // Health Monitor) — they live in the More Apps page, not the chat catalog.
        agents.filter(a =>
          (a.outputType ?? '').toLowerCase() !== 'app' &&
          !HIDDEN_SLASH_COMMANDS.has(a.command.toLowerCase())),
      );
    });
    this.catalogSub = this.catalogSvc.commandSelected$.subscribe(cmd => {
      this._setInputValue(cmd);
      this.slashMenuOpen.set(false);
      this._focusInputAtEnd();
      // Catalog pick (command palette / dashboards gallery) — an explicit
      // agent choice. Arm the one-shot selected_agent_id fast-path hint for
      // this pick's first ordinary send (see _pendingExplicitAgentPick).
      const picked = this._agentIdForCommand(cmd);
      if (picked) this._pendingExplicitAgentPick = picked;
    });
    this.stateSub = this.wsService.connectionState$.subscribe(state => {
      if (this.isDestroying) return;
      if (state === 'connected') this._socketConnected = true;
      this.isConnecting.set(state === 'connecting');
      // Only treat a drop as a genuine turn-ending failure when either it's an
      // 'error', or it's a 'disconnected' AFTER we had actually connected. The
      // intentional teardown inside _connectWs (connect() calls disconnect(),
      // which synchronously emits 'disconnected' before the new socket opens)
      // must NOT reset the in-flight turn — otherwise the thinking placeholder
      // is detached (_currentAgentMsgIdx → -1) and the streamed reply lands
      // nowhere. _socketConnected is reset to false at the top of _connectWs.
      const droppedMidTurn = state === 'error' || (state === 'disconnected' && this._socketConnected);
      if (droppedMidTurn && this.isInFlight()) {
        this._socketConnected = false;
        this.isInFlight.set(false);
        this._markCurrentAgentMsgDone();
      }
    });
    // Eager socket: open the WS while the user is still reading the welcome
    // screen / typing, so the $connect handshake (and the server-side prewarm
    // it triggers) overlaps composition instead of adding to the first send.
    // The send paths keep their lazy connect as fallback for when this socket
    // isn't open by then (rejected handshake, drop, deleted session, …).
    this._eagerConnectWs();
  }

  ngOnDestroy(): void {
    this.isDestroying = true;
    clearTimeout(this._stillWorkingTimer);
    clearTimeout(this._copyResetTimer);
    clearTimeout(this._resumePollTimer);
    this.wsSub?.unsubscribe();
    this.stateSub?.unsubscribe();
    this.resumeSub?.unsubscribe();
    this.catalogSub?.unsubscribe();
    this.userNameSub?.unsubscribe();
    this.artifactActionSub?.unsubscribe();
    this.navSub?.unsubscribe();
    this.wsService.disconnect();
  }

  // ── Sessions panel ────────────────────────────────────────────

  get filteredSessions(): SessionSummary[] {
    const list = this.sessions() ?? [];
    const q = this.sessionSearch().toLowerCase().trim();
    if (!q) return list;
    return list.filter(s => this._sessionMatchesQuery(s, q));
  }

  /**
   * Recents grouped for display. In "none" mode the whole list is returned as a
   * single unlabelled group (renders as a flat list under the Recents head). In
   * "date" mode rows are bucketed by recency: Today / Yesterday / Previous 7
   * days / Older. Empty buckets are dropped, and existing (newest-first) order
   * is preserved within each.
   */
  get sessionGroups(): SessionGroup[] {
    const list = this.filteredSessions;
    if (!list.length) return [];
    if (this.groupBy() !== 'date') {
      return [{ key: 'all', label: '', sessions: list }];
    }

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86_400_000;
    const startOf7Days = startOfToday - 7 * 86_400_000;

    const today: SessionSummary[] = [];
    const yesterday: SessionSummary[] = [];
    const week: SessionSummary[] = [];
    const older: SessionSummary[] = [];

    for (const s of list) {
      const t = s.updated_at * 1000;
      if (t >= startOfToday)          today.push(s);
      else if (t >= startOfYesterday) yesterday.push(s);
      else if (t >= startOf7Days)     week.push(s);
      else                            older.push(s);
    }

    const groups: SessionGroup[] = [];
    if (today.length)     groups.push({ key: 'today',     label: 'Today',          sessions: today });
    if (yesterday.length) groups.push({ key: 'yesterday', label: 'Yesterday',      sessions: yesterday });
    if (week.length)      groups.push({ key: 'week',      label: 'Previous 7 days', sessions: week });
    if (older.length)     groups.push({ key: 'older',     label: 'Older',          sessions: older });
    return groups;
  }

  /** PrimeIcons class for a session row, chosen from its routed agent id.
   * Each agent gets a distinct glyph that hints at what it does. IMPACT is
   * checked before PCHECK (IMPACT_PCHECK contains "PCHECK") so the two
   * meeting agents don't collapse to the same icon. */
  sessionIcon(agentId: string | null): string {
    const id = (agentId ?? '').toUpperCase();
    if (!id) return 'pi-comments';
    if (id.includes('IMPACT'))                            return 'pi-briefcase';  // executive pulse-check
    if (id.includes('PCHECK'))                            return 'pi-calendar';   // pulse-check booking
    if (id.includes('COMPETITOR'))                        return 'pi-trophy';     // win/loss analysis
    if (id.includes('SF') || id.includes('SALESFORCE'))   return 'pi-chart-line'; // account summaries
    if (id.includes('ATLASSIAN') || id.includes('JIRA'))  return 'pi-ticket';     // jira / confluence
    if (id.includes('NEAT'))                              return 'pi-file-edit';  // deal-analysis report
    if (id.includes('SKU'))                               return 'pi-tag';        // product SKUs
    if (id.includes('AI_ROLE'))                           return 'pi-user-edit';  // role transformation
    if (id.includes('LICEXT'))                            return 'pi-key';        // device/SaaS license extension
    return 'pi-comments';
  }

  get modalSearchSessions(): SessionSummary[] {
    const list = this.sessions() ?? [];
    const q = this.sessionSearch().toLowerCase().trim();
    if (!q) return list;
    return list.filter(s => this._sessionMatchesQuery(s, q));
  }

  get managerFilteredSessions(): SessionSummary[] {
    const list = this.sessions() ?? [];
    const q = this.managerSearch().toLowerCase().trim();
    if (!q) return list;
    return list.filter(s => this._sessionMatchesQuery(s, q));
  }

  get selectedCount(): number {
    return this.selectedSessionIds().size;
  }

  get allManagerSelected(): boolean {
    const visible = this.managerFilteredSessions;
    return visible.length > 0 && visible.every(s => this.selectedSessionIds().has(s.session_id));
  }

  private _sessionMatchesQuery(s: SessionSummary, q: string): boolean {
    return (s.title?.toLowerCase().includes(q) ?? false)
      || (s.active_agent_id?.toLowerCase().includes(q) ?? false)
      || (s.summary_preview?.toLowerCase().includes(q) ?? false);
  }

  private _loadSessions(): void {
    this.sessionsLoading.set(true);
    this.sessionListSvc.getSessions().subscribe({
      next: res => {
        this.sessions.set(this._applyPendingRenames(res.sessions));
        this.sessionsLoading.set(false);
      },
      error: () => this.sessionsLoading.set(false),
    });
  }

  private _loadSuggestions(): void {
    this.suggestionSvc.getSuggestions().subscribe({
      next: chips => this.suggestions.set(chips),
      error: () => {},
    });
  }

  onSessionSearchInput(event: Event): void {
    this.sessionSearch.set((event.target as HTMLInputElement).value);
    this.searchHighlightIdx.set(0);
  }

  onManagerSearchInput(event: Event): void {
    this.managerSearch.set((event.target as HTMLInputElement).value);
  }

  openSessionSearch(): void {
    this.sessionSearchOpen.set(true);
    this.searchHighlightIdx.set(0);
    setTimeout(() => this.sessionSearchInput?.nativeElement?.focus(), 50);
  }

  closeSessionSearch(): void {
    this.sessionSearchOpen.set(false);
    this.sessionSearch.set('');
    this.searchHighlightIdx.set(0);
  }

  onSearchModalSelect(session: SessionSummary): void {
    this.closeSessionSearch();
    this.onSelectSession(session);
  }

  onSearchModalKeydown(event: KeyboardEvent): void {
    const list = this.modalSearchSessions;
    if (!list.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.searchHighlightIdx.update(i => Math.min(i + 1, list.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.searchHighlightIdx.update(i => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const idx = this.searchHighlightIdx();
      if (list[idx]) this.onSearchModalSelect(list[idx]);
    }
  }

  openSessionsManager(): void {
    this.sessionsManagerOpen.set(true);
    this.managerSearch.set('');
    this.selectedSessionIds.set(new Set());
    this.bulkDeleteConfirm.set(false);
    this.userMenuOpen.set(false);
    setTimeout(() => this.managerSearchInput?.nativeElement?.focus(), 50);
  }

  closeSessionsManager(): void {
    this.sessionsManagerOpen.set(false);
    this.selectedSessionIds.set(new Set());
    this.bulkDeleteConfirm.set(false);
  }

  toggleSessionsSection(): void {
    this.sessionsSectionExpanded.update(v => !v);
  }

  toggleGroupByMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.groupByMenuOpen.update(v => !v);
  }

  setGroupBy(mode: 'none' | 'date'): void {
    this.groupBy.set(mode);
    this.groupByMenuOpen.set(false);
  }

  isSessionSelected(id: string): boolean {
    return this.selectedSessionIds().has(id);
  }

  toggleSessionSelected(id: string, event?: Event): void {
    event?.stopPropagation();
    this.selectedSessionIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    this.bulkDeleteConfirm.set(false);
  }

  toggleSelectAllManager(): void {
    const visible = this.managerFilteredSessions;
    if (this.allManagerSelected) {
      this.selectedSessionIds.update(set => {
        const next = new Set(set);
        visible.forEach(s => next.delete(s.session_id));
        return next;
      });
    } else {
      this.selectedSessionIds.update(set => {
        const next = new Set(set);
        visible.forEach(s => next.add(s.session_id));
        return next;
      });
    }
    this.bulkDeleteConfirm.set(false);
  }

  onBulkDeleteRequest(): void {
    if (!this.selectedCount) return;
    this.bulkDeleteConfirm.set(true);
  }

  onBulkDeleteCancel(): void {
    this.bulkDeleteConfirm.set(false);
  }

  onBulkDeleteConfirm(): void {
    const ids = [...this.selectedSessionIds()];
    if (!ids.length) return;
    this.bulkDeleteConfirm.set(false);
    const activeId = this.sessionId();
    this.sessions.update(list => list.filter(s => !ids.includes(s.session_id)));
    if (activeId && ids.includes(activeId)) {
      this.wsSub?.unsubscribe();
      this.wsService.disconnect();
      this.sessionId.set(null);
      this.messages.set([]);
      this._clearInput();
      this.isInFlight.set(false);
      this.activeAgentId.set(null);
      this.agentState.set('HOME');
    }
    this.selectedSessionIds.set(new Set());
    forkJoin(ids.map(id => this.sessionListSvc.deleteSession(id))).subscribe();
    if (ids.length === this.sessions().length) {
      this.closeSessionsManager();
    }
  }

  toggleSidebar(): void {
    // Below md the sidebar can't expand inline — toggle the p-drawer instead.
    // The brand button on the collapsed rail opens the drawer; the collapse
    // button inside the expanded content (rendered in the drawer on mobile)
    // closes it. Both routes hit this method.
    if (!this.bp.isDesktop()) {
      this.drawerOpen.update(v => !v);
      if (this.drawerOpen()) this.userMenuOpen.set(false);
      return;
    }
    this.sidebarCollapsed.update(v => !v);
    if (this.sidebarCollapsed()) this.userMenuOpen.set(false);
  }

  onNewChat(): void {
    // On the data tab, always navigate back to the chat welcome screen.
    if (this.currentView() === 'data') {
      this.router.navigate(['/chat']);
      return;
    }
    // Already on a fresh welcome screen (no session, no messages)? Do nothing —
    // clicking New chat again must not re-roll the greeting (matches ChatGPT/Claude).
    if (this.sessionId() === null && this.messages().length === 0) return;
    // Coming from a live chat — disconnect and return to a new welcome screen
    // with a freshly-rolled greeting.
    this.pickGreeting();
    this.wsSub?.unsubscribe();
    this.wsService.disconnect();
    this.sessionId.set(null);
    this.messages.set([]);
    this._clearInput();
    this.isInFlight.set(false);
    this.isConnecting.set(false);
    this.activeAgentId.set(null);
    this.agentState.set('HOME');
    this._currentAgentMsgIdx = -1;
    this._pendingActionText = null;
    this._pendingExplicitAgentPick = null;
    this.artifacts.reset();
    // Pre-open the socket for the next conversation (same rationale as the
    // ngOnInit eager connect — the user is back on the composer, about to type).
    this._eagerConnectWs();
  }

  onOpenCatalog(): void {
    this.catalogSvc.triggerOpen();
  }

  /** Open the read-only Abilities modal (browse-only list, like the catalog). */
  openAbilities(): void {
    this.abilitiesSvc.triggerOpen();
  }

  // ── Onboarding: guided tour + What's New ───────────────────────

  /** Sparkle button: the full What's New changelog, on demand. */
  openWhatsNew(): void {
    this.userMenuOpen.set(false);
    this.whatsNew.openAll();
  }

  /** Guards _runOnboardingOnce — the surfaces effect and the ngOnInit fallback
   *  timer both call it, whichever fires first wins. */
  private _onboardingRan = false;

  /** First visit: run the guided tour; the What's New panel then pops once
   *  right after it ends (tour-end effect in the constructor) so announcements
   *  are never silently swallowed. Returning visits: pop What's New only when
   *  something unseen shipped. Runs once per component lifetime. */
  private _runOnboardingOnce(): void {
    if (this._onboardingRan || this.isDestroying) return;
    this._onboardingRan = true;
    if (!this.tourSvc.hasCompleted(AGENT_HUB_TOUR.id)) {
      this.tourSvc.requestStart(AGENT_HUB_TOUR);
    } else {
      // Re-fetch on the home visit (the service otherwise only fetches once at
      // app boot), then auto-open if something is unseen — so an announcement
      // published while this session was open still surfaces.
      this.whatsNew.refreshThenMaybeAutoOpen();
    }
  }

  // ── User menu ──────────────────────────────────────────────────

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen.update(v => !v);
  }

  // ── Send Feedback ──────────────────────────────────────────────

  openFeedback(): void {
    this.userMenuOpen.set(false);
    this.feedbackOpen.set(true);
  }

  closeFeedback(): void {
    this.feedbackOpen.set(false);
  }

  /** Current conversation as plain {role, content} turns for the feedback payload. */
  feedbackConversation(): ConversationTurn[] {
    return this.messages()
      .map(m => ({
        role: (m.role === 'agent' ? 'assistant' : 'user') as ConversationTurn['role'],
        content: String(m.content ?? '').trim(),
      }))
      .filter(turn => turn.content.length > 0);
  }

  /** Display name of the active agent (for the feedback record/email), if known. */
  activeAgentName(): string | null {
    const id = this.activeAgentId();
    if (!id) return null;
    return this.catalogAgents().find(a => (a.agentId ?? '') === id)?.displayName ?? null;
  }

  @HostListener('document:click', ['$event'])
  closeUserMenu(event: MouseEvent): void {
    const t = event.target as HTMLElement;
    if (!t.closest('.ah-user-btn, .ah-rail-btn--avatar, .ah-user-menu, .ah-rail__user-wrap')) {
      this.userMenuOpen.set(false);
    }
    if (!t.closest('.ah-groupby')) {
      this.groupByMenuOpen.set(false);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (this.sessionsManagerOpen()) return;
      this.openSessionSearch();
      return;
    }
    if (event.key === 'Escape') {
      if (this.sessionSearchOpen()) { this.closeSessionSearch(); return; }
      if (this.sessionsManagerOpen()) { this.closeSessionsManager(); return; }
      if (this.groupByMenuOpen()) { this.groupByMenuOpen.set(false); return; }
      if (this.userMenuOpen()) { this.userMenuOpen.set(false); }
    }
  }

  openAdminPortal(): void {
    this.userMenuOpen.set(false);
    this.audit.trackEvent({ action_type: 'NAVIGATE', description: 'Opened Admin Portal' });
    // Session hand-off lives in HubDestinationsService so the sidebar tiles and
    // What's New CTAs open the portals identically.
    this.hubDest.openPortal(environment.ADMIN_PORTAL_URL);
  }

  openStudioPortal(): void {
    this.userMenuOpen.set(false);
    this.hubDest.openPortal(environment.STUDIO_PORTAL_URL);
  }

  openDevToolsPortal(): void {
    this.userMenuOpen.set(false);
    this.audit.trackEvent({ action_type: 'NAVIGATE', description: 'Opened Dev Tools Portal' });
    this.hubDest.openPortal(environment.DEVTOOLS_PORTAL_URL);
  }

  openAppsPortal(): void {
    this.userMenuOpen.set(false);
    this.audit.trackEvent({ action_type: 'NAVIGATE', description: 'Opened Apps Portal' });
    this.hubDest.openPortal(environment.APPS_PORTAL_URL);
  }

  openHelpPortal(): void {
    this.userMenuOpen.set(false);
    this.audit.trackEvent({ action_type: 'NAVIGATE', description: 'Opened Help Portal' });
    this.hubDest.openPortal(environment.HELP_PORTAL_URL);
  }

  onLogout(): void {
    this.userMenuOpen.set(false);
    this.audit.trackEvent({ action_type: 'LOGOUT', description: 'User initiated logout' });
    this.auth.logout();
    this.router.navigate(['/login']).then(() => {
      try {
        const accounts = this.msal.instance.getAllAccounts();
        if (accounts.length > 0) this.msal.logoutRedirect({ account: accounts[0] });
      } catch { /* best-effort */ }
    });
  }

  onSelectSession(session: SessionSummary): void {
    if (this.currentView() === 'data') this.router.navigate(['/chat']);
    this._openSession(session.session_id, true);
  }

  onDeleteSession(session: SessionSummary): void {
    this.confirmDeleteId.set(session.session_id);
  }

  onConfirmDelete(session: SessionSummary): void {
    this.confirmDeleteId.set(null);
    this.sessions.update(list => list.filter(s => s.session_id !== session.session_id));
    if (this.sessionId() === session.session_id) {
      this.sessionId.set(null);
      this.messages.set([]);
      this.wsService.disconnect();
    }
    this.sessionListSvc.deleteSession(session.session_id).subscribe();
  }

  onCancelDelete(): void {
    this.confirmDeleteId.set(null);
  }

  /** Enter inline-edit mode for a session, seeding the draft with its title. */
  onStartRename(session: SessionSummary): void {
    this.confirmDeleteId.set(null);
    this.renameDraft.set(session.title ?? '');
    this.renameId.set(session.session_id);
  }

  /** Commit the inline rename: trim, optimistically update the list, persist.
   * Idempotent — Enter fires this and then blur fires it again; the guard makes
   * the second call (and a post-Escape blur, where renameId is already null) a
   * no-op so we never double-save or resurrect a cancelled edit. */
  onConfirmRename(session: SessionSummary): void {
    if (this.renameId() !== session.session_id) return;
    const title = this.renameDraft().trim().slice(0, 60);
    this.renameId.set(null);
    if (!title || title === session.title) return;
    const previous = session.title;
    const id = session.session_id;
    // Optimistic: show the new title and shield it from refreshes until the
    // server confirms (see _applyPendingRenames).
    this._pendingRenames.set(id, title);
    this._setSessionTitle(id, title);
    this.sessionListSvc.renameSession(id, title).subscribe(saved => {
      if (saved === null) {
        // Write failed — stop shielding and roll back so the sidebar never
        // shows a title that wasn't saved.
        this._pendingRenames.delete(id);
        this._setSessionTitle(id, previous);
      } else {
        // Persisted. Track the server-accepted title (it may differ if trimmed)
        // so refreshes stay stable through the read-after-write window.
        this._pendingRenames.set(id, saved);
        this._setSessionTitle(id, saved);
      }
    });
  }

  private _setSessionTitle(sessionId: string, title: string): void {
    this.sessions.update(list =>
      list.map(s => (s.session_id === sessionId ? { ...s, title } : s)));
  }

  /** Overlay unconfirmed optimistic renames onto a freshly-fetched list, and
   *  drop entries the server has caught up on. Bridges the GSI read-after-write
   *  window without masking failed writes (those are removed in onConfirmRename). */
  private _applyPendingRenames(list: SessionSummary[]): SessionSummary[] {
    if (!this._pendingRenames.size) return list;
    return list.map(s => {
      const pending = this._pendingRenames.get(s.session_id);
      if (pending === undefined) return s;
      if (s.title === pending) { this._pendingRenames.delete(s.session_id); return s; }
      return { ...s, title: pending };
    });
  }

  onCancelRename(): void {
    this.renameId.set(null);
    this.renameDraft.set('');
  }

  private _openSession(sid: string, resume: boolean): void {
    this.sessionId.set(sid);
    this.messages.set([]);
    this.activeAgentId.set(null);
    this.agentState.set('HOME');
    this._currentAgentMsgIdx = -1;
    this._selectedContactEmail = '';
    this._additionalGuests = [];
    this._prepDocAutoTriggered = false;
    this._pendingSilentTrigger = false;
    this._pendingExplicitAgentPick = null;
    this.artifacts.reset();
    this._connectWs(sid, resume);
  }

  // ── WebSocket ─────────────────────────────────────────────────

  private _connectWs(sid: string, resume: boolean): void {
    this.wsSub?.unsubscribe();
    // Cancel any pending resume from a previous selection. Without this, a
    // rapid second click would leave the first session's resumeSub alive — it
    // would then fire on the new socket's 'connected' state and send a resume
    // for the WRONG session_id, racing with the current selection's resume.
    this.resumeSub?.unsubscribe();
    this.resumeSub = undefined;
    // Same reasoning as the resumeSub cancellation above: a stale poll timer
    // left running from a previous session's resume must never fire a resume
    // request tagged with the WRONG (now-abandoned) session's socket state.
    clearTimeout(this._resumePollTimer);
    this._resumePollTimer = undefined;
    this._resumePollAttempts = 0;
    this.resumePending.set(false);
    // New connection attempt: mark "not yet connected" so the synchronous
    // 'disconnected' that connect()→disconnect() emits while tearing down the
    // prior socket is treated as intentional teardown, not a mid-turn drop.
    this._socketConnected = false;
    this.isConnecting.set(true);
    const wsUrl = environment.WG_AGENTS_WS_URL;
    try {
      this.wsSub = this.wsService.connect(wsUrl, sid, 'AGENTHOME').subscribe({
        // WsEvent is a discriminated union — variants don't carry an index
        // signature, so cast through `unknown` first; _handleWsEvent reads
        // ev['type'] and additional payload fields by key access against the
        // loose dict shape.
        next: ev => this._handleWsEvent(ev as unknown as Record<string, unknown>),
        error: () => { this.isConnecting.set(false); },
      });
    } catch {
      this.isConnecting.set(false);
      return;
    }

    if (resume) {
      this._isResumingSession = true;
      this.resumeSub = this.wsService.connectionState$.subscribe(state => {
        if (state === 'connected') {
          this.resumeSub?.unsubscribe();
          this.resumeSub = undefined;
          this._sendResumeRequest(sid);
        }
      });
    }
  }

  /** Sends `action_kind: 'resume'` and (re)seeds a single placeholder agent
   *  message so `history`/`done` have a valid _currentAgentMsgIdx to update;
   *  it's removed silently once the resume turn actually completes so no
   *  "Welcome back" text is ever shown.
   *
   *  Used both for the initial resume send and for every poll resend
   *  (_scheduleResumePoll): a poll resend must reset `messages` back to just
   *  this placeholder FIRST, not append to it — the `history` frame that
   *  follows always replays the FULL persisted conversation window, so
   *  appending a second time would duplicate every already-rendered turn. */
  private _sendResumeRequest(sid: string): void {
    this.messages.set([{
      id: ++this._msgId, role: 'agent', content: '', isThinking: true,
    }]);
    this._currentAgentMsgIdx = 0;
    this._sendTurnFrame(sid, '', { action: 'agenthome_chat', action_kind: 'resume' });
    this.isInFlight.set(true);
  }

  /** Re-send `action_kind: 'resume'` after a delay — used only when the
   *  previous resume `done` frame reported turn_status: "running" (the user
   *  left mid-turn and the backend is still working on it). Keeps polling
   *  until the backend reports a terminal status or RESUME_POLL_MAX_ATTEMPTS
   *  is hit, at which point it gives up gracefully rather than spinning
   *  forever — the user can still reopen the session later to check again.
   *  Guards every fire against having since navigated to a different session
   *  (sid mismatch) or torn down the component. */
  private _scheduleResumePoll(sid: string): void {
    clearTimeout(this._resumePollTimer);
    if (this._resumePollAttempts >= RESUME_POLL_MAX_ATTEMPTS) {
      this.resumePending.set(false);
      this.isInFlight.set(false);
      this._isResumingSession = false;
      this._updateAgentMsg(m => ({
        ...m, isThinking: false,
        content: "This is taking longer than expected. Reopen this session in a bit to see the reply.",
      }));
      this._currentAgentMsgIdx = -1;
      return;
    }
    this._resumePollAttempts++;
    this._resumePollTimer = setTimeout(() => {
      if (this.isDestroying || this.sessionId() !== sid) return;
      this._sendResumeRequest(sid);
    }, RESUME_POLL_INTERVAL_MS);
  }

  /** Pre-open the AGENTHOME socket for a not-yet-started conversation, so the
   *  handshake happens while the user is still composing. No-op when a session
   *  is active or a connection attempt is already under way. Pre-generates the
   *  session id the first send will adopt (shared with the welcome-screen
   *  upload flow); AGENTHOME reads session_id per frame, so the send path still
   *  works even if that id is later discarded (e.g. clearAttachment). */
  private _eagerConnectWs(): void {
    if (this.isDestroying || this.sessionId()) return;
    const state = this.wsService.connectionState$.value;
    if (state === 'connected' || state === 'connecting') return;
    this._pendingUploadSessionId ??= generateSessionId();
    this._connectWs(this._pendingUploadSessionId, false);
  }

  // ── Send ──────────────────────────────────────────────────────

  onSendMessage(): void {
    if (this.slashMenuOpen()) return;
    if (this.usageBlocked()) return;  // over quota — server would reject anyway
    const text = this.currentInput().trim();
    if (!text || this.isInFlight() || this.isUploading()) return;  // block send while a file upload is still in flight

    const sid = this.sessionId();
    if (!sid) {
      // No session yet — reuse pending upload session ID if a file was already
      // uploaded from the welcome screen, otherwise generate a fresh one.
      const newSid = this._pendingUploadSessionId ?? generateSessionId();
      this._pendingUploadSessionId = null;
      this.sessionId.set(newSid);
      this.messages.set([]);
      // Render the bubble + thinking placeholder immediately so the user sees
      // progress while the socket connects; defer only the send to `connected`.
      const { text: finalText, extra } = this._composeAndRender(text);
      // The eager socket (see _eagerConnectWs) is normally already open here —
      // send straight away. Otherwise fall back to lazy connect and defer the
      // send to `connected` (the pre-eager behavior); 'connecting' means an
      // attempt is in flight, so only subscribe.
      const state = this.wsService.connectionState$.value;
      if (state === 'connected') {
        this._sendTurnFrame(newSid, finalText, extra);
        return;
      }
      if (state !== 'connecting') this._connectWs(newSid, false);
      let sub: Subscription | undefined;
      sub = this.wsService.connectionState$.subscribe(s => {
        if (s === 'connected') {
          sub?.unsubscribe();
          this._sendTurnFrame(newSid, finalText, extra);
        }
      });
      return;
    }
    this._sendMessage(sid, text);
  }

  /**
   * Render the outgoing turn optimistically — push the user bubble + a thinking
   * placeholder, flip `isInFlight`, clear the input — and return the resolved
   * `{ text, extra }` to send. Split out from `_sendMessage` so a new chat can
   * show this feedback *before* the WebSocket connects (the send is deferred to
   * the `connected` event) instead of leaving the screen blank during connect.
   */
  private _composeAndRender(text: string): { text: string; extra: Record<string, unknown> } {
    // If a file was uploaded, append the filename so the agent can reference it.
    const attachment = this.pendingAttachment();
    const extra: Record<string, unknown> = { action: 'agenthome_chat', action_kind: 'chat' };
    // Explicit-pick fast path: an ordinary (non-slash) send may carry the
    // one-shot selected_agent_id so the server direct-dispatches. Slash sends
    // don't consume the pick (that would burn it before its follow-up, e.g. the
    // bare /switch turn that precedes a silent replay) — instead they resolve
    // the typed command against the catalog THIS client fetched and send that
    // agent id alongside. The server routes catalog commands from the same
    // table, but if its map is behind (an agent published seconds ago) the
    // hint lets it honour the command instead of answering "unknown command".
    if (!text.trimStart().startsWith('/')) {
      Object.assign(extra, this._consumeExplicitPick());
    } else {
      const commandAgent = this._agentIdForCommand(text);
      if (commandAgent) extra['selected_agent_id'] = commandAgent;
    }
    if (attachment) {
      text = `${text}\n\n[Attached file: ${attachment.filename}]`;
      extra['upload_filename'] = attachment.filename;
      // NOTE: do NOT clear pendingAttachment here. If this send only triggers a
      // router clarification (e.g. "switch to NEAT or finish booking?") rather
      // than reaching the target agent, clearing now would drop upload_filename
      // from the follow-up ("yes switch") turn — so NEAT would never receive a
      // clean file reference (no deal-context wizard, no source_filename, hence
      // no Reanalyze button). Instead we keep it until the file is actually
      // consumed (_consumePendingAttachment, called on neat_ui / artifact /
      // download_ready), the user removes it, or the turn completes and the
      // backend's `keep_upload` flag says the file is no longer in play (the
      // 'done' handler clears it then so it can't re-attach to every later
      // message). It only survives a turn when the router kept it for a pending
      // routing choice.
    }
    this.messages.update(msgs => [...msgs, { id: ++this._msgId, role: 'user', content: text }]);
    this._clearInput();
    this.slashMenuOpen.set(false);
    this.isInFlight.set(true);
    this.messages.update(msgs => [...msgs, {
      id: ++this._msgId, role: 'agent', content: '', isThinking: true,
    }]);
    this._currentAgentMsgIdx = this.messages().length - 1;
    this._scrollToBottom(true);
    return { text, extra };
  }

  /**
   * Every frame that STARTS a turn (chat, chip, resume, silent trigger,
   * widget action) goes through here so it carries a fresh client-minted
   * `turn_id`. AGENTHOME echoes it on each frame of that turn and the WS
   * router names it in a later Stop request. Minted at send time (not at
   * compose time) so a send deferred until the socket connects still tags
   * the turn that actually goes on the wire.
   */
  private _sendTurnFrame(sid: string, text: string, extra?: Record<string, unknown>): void {
    const turnId = generateTurnId();
    this._activeTurnId = turnId;
    this.wsService.send(sid, text, { ...(extra ?? {}), turn_id: turnId });
  }

  /**
   * Stop button / Esc. Two halves, both required:
   *
   *  1. SERVER — send `action_kind: 'cancel'` for the active turn. The WS
   *     router relays it to AGENTHOME's /agenthome/cancel, which signals the
   *     running turn (in-process if it owns it, via a session-row marker the
   *     turn polls otherwise). The runtime stops the model stream / the
   *     downstream relay at its next check, persists what the user has seen
   *     with a stopped marker, and closes its stream with `done` (stopped:
   *     true). A new message sent before that lands supersedes the old turn
   *     server-side (it persists nothing then), so sending right after Stop
   *     is safe.
   *  2. LOCAL — finalise the bubble now (keep the partial text, mark it
   *     Stopped, resolve running pills), unlock the composer, and swallow any
   *     late frames tagged with this turn's id until its close-out arrives.
   *
   * While a resumed session is still "working on your last message"
   * (resumePending), only the server half applies, with no turn id — the
   * cancel targets whichever turn owns the row — and the resume poll keeps
   * going: the next poll sees the turn finished and replays the history,
   * stopped marker included.
   */
  onStop(): void {
    if (!this.isInFlight()) return;
    const sid = this.sessionId();
    if (!sid) return;
    if (this._isResumingSession) {
      this.wsService.send(sid, '', { action: 'agenthome_chat', action_kind: 'cancel' });
      return;
    }
    const turnId = this._activeTurnId;
    const extra: Record<string, unknown> = { action: 'agenthome_chat', action_kind: 'cancel' };
    if (turnId) extra['turn_id'] = turnId;
    this.wsService.send(sid, '', extra);

    if (turnId) {
      this._stoppedTurnIds.add(turnId);
      if (this._stoppedTurnIds.size > 20) {
        const oldest = this._stoppedTurnIds.values().next().value;
        if (oldest) this._stoppedTurnIds.delete(oldest);
      }
    }
    this._updateAgentMsg(m => ({
      ...m,
      isThinking: false,
      isStopped: true,
      toolActivity: m.toolActivity?.map(a =>
        a.status === 'running' ? { ...a, status: 'done' as const } : a),
    }));
    this._currentAgentMsgIdx = -1;
    this.isInFlight.set(false);
    this.stillWorking.set(false);
    // Drop any queued widget action — the user is changing course.
    this._pendingActionText = null;
    this._scrollToBottom();
    setTimeout(() => this.chatTextarea?.nativeElement.focus(), 0);
  }

  private _sendMessage(sid: string, text: string): void {
    const { text: finalText, extra } = this._composeAndRender(text);
    this._sendTurnFrame(sid, finalText, extra);
  }

  /**
   * Consume the one-shot explicit agent pick (catalog / slash-menu / chip) for
   * an ordinary chat send. Returns `{ selected_agent_id }` — the server's
   * direct-dispatch fast path, skipping its ~2.5s router LLM pass — only when
   * the pick still matches the session's active agent (a routing change since
   * the pick stales it). The pick clears either way, so it can never leak onto
   * a later typed message and bypass the supervisor router (free-text
   * re-routing like "switch to X" must always reach it).
   */
  private _consumeExplicitPick(): Record<string, unknown> {
    const pick = this._pendingExplicitAgentPick;
    this._pendingExplicitAgentPick = null;
    return pick && pick === this.activeAgentId() ? { selected_agent_id: pick } : {};
  }

  /** agent_id behind a catalog command string ("/sf …" → SF_AGENT), or null
   *  when the first token doesn't name a catalog agent. Case-insensitive with
   *  "-" folded to "_" — the same normalization the runtime applies
   *  (shared/command_contract.normalize_command), so "/Ask-Salesforce" resolves. */
  private _agentIdForCommand(cmd: string): string | null {
    const token = normalizeSlashCommand(cmd.trim().split(/\s+/)[0] ?? '');
    if (!token.startsWith('/')) return null;
    return this.catalogAgents().find(a => normalizeSlashCommand(a.command ?? '') === token)?.agentId ?? null;
  }

  /** Send a backend trigger without rendering a visible user message bubble.
   *  Propagates the user's original intent (last non-slash user message) so
   *  the switched-to agent can pick up the task without re-asking. */
  private _silentTrigger(sid: string): void {
    const originalIntent = [...this.messages()]
      .reverse()
      .find(m => m.role === 'user' && !m.content.startsWith('/'))
      ?.content ?? 'hello';
    this.isInFlight.set(true);
    // The switch turn just finished as an empty agent bubble carrying only the
    // "Switched to X" pill. Reuse it as the welcome target — keep its pills,
    // start it thinking — instead of appending a NEW empty bubble below it. A
    // separate bubble would strand the pill far above the welcome text (an empty
    // bubble + the inter-message gap); reusing it renders the pill atop the
    // welcome in one bubble, matching a normal tool-activity turn.
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    const reuseSwitchBubble = !!last
      && last.role === 'agent' && !last.content && !last.artifact
      && !!last.toolActivity?.length;
    if (reuseSwitchBubble) {
      this._currentAgentMsgIdx = msgs.length - 1;
      this._updateAgentMsg(m => ({ ...m, isThinking: true }));
    } else {
      this.messages.update(ms => [...ms, {
        id: ++this._msgId, role: 'agent' as const, content: '', isThinking: true,
      }]);
      this._currentAgentMsgIdx = this.messages().length - 1;
    }
    this._scrollToBottom(true);
    // The replayed intent goes to the agent the user JUST explicitly picked
    // (the bare /switch turn set it active) — carry selected_agent_id so the
    // server direct-dispatches instead of re-running the router LLM pass.
    this._sendTurnFrame(sid, originalIntent, {
      action: 'agenthome_chat', action_kind: 'chat', ...this._consumeExplicitPick(),
    });
  }

  onSuggestionChipClick(chip: Suggestion): void {
    let sid = this.sessionId();
    if (!sid) {
      sid = generateSessionId();
      this.sessionId.set(sid);
      this.messages.set([]);
      // Reuse the eager socket when it's open (or mid-handshake) instead of
      // tearing it down for a reconnect; the connected-state check below
      // already defers the send until the socket is ready.
      const state = this.wsService.connectionState$.value;
      if (state !== 'connected' && state !== 'connecting') this._connectWs(sid, false);
    }
    const targetSid = sid;
    // Render the bubble + thinking placeholder immediately, then send once the
    // socket is (or becomes) connected — so a new chat shows progress at once.
    const { text: finalText, extra } = this._composeAndRender(chip.prompt);
    if (this.wsService.connectionState$.value === 'connected') {
      this._sendTurnFrame(targetSid, finalText, extra);
    } else {
      let sub: Subscription | undefined;
      sub = this.wsService.connectionState$.subscribe(state => {
        if (state === 'connected') {
          sub?.unsubscribe();
          this._sendTurnFrame(targetSid, finalText, extra);
        }
      });
    }
  }

  /** Confirm-before-write: answer the card. Locks it immediately (no double
   *  sends), then puts the server-built confirm/reject message on the wire
   *  VERBATIM while the user bubble shows a human label — the token is
   *  transport, not conversation. */
  onActionProposalAnswer(messageId: number, confirmed: boolean): void {
    const sid = this.sessionId();
    if (!sid) return;
    const msg = this.messages().find(m => m.id === messageId);
    const proposal = msg?.actionProposal;
    if (!proposal || msg?.actionProposalResolved) return;
    this.messages.update(msgs => msgs.map(m =>
      m.id === messageId
        ? { ...m, actionProposalResolved: confirmed ? 'confirmed' as const : 'rejected' as const }
        : m));
    const raw = confirmed ? proposal.confirmMessage : proposal.rejectMessage;
    const display = confirmed ? ACTION_CONFIRM_DISPLAY : ACTION_REJECT_DISPLAY;
    this.messages.update(msgs => [...msgs, { id: ++this._msgId, role: 'user', content: display }]);
    this.isInFlight.set(true);
    this.messages.update(msgs => [...msgs, {
      id: ++this._msgId, role: 'agent', content: '', isThinking: true,
    }]);
    this._currentAgentMsgIdx = this.messages().length - 1;
    this._scrollToBottom(true);
    this._sendTurnFrame(sid, raw, { action: 'agenthome_chat', action_kind: 'chat' });
  }

  onAgentSuggestionClick(chip: SuggestionChip): void {
    const sid = this.sessionId();
    if (!sid) return;
    // Clear suggestions from all messages so the option list is no longer interactive.
    this.messages.update(msgs => msgs.map(m => m.suggestions?.length ? { ...m, suggestions: [] } : m));
    // Prefer the server-supplied value when present; fall back to
    // "/switch <agent_id>" for plain agent-choice chips that omit value.
    const msg = chip.value ?? `/switch ${chip.agent_id}`;
    // Replay the user's original intent after the switch ONLY when we send a
    // BARE "/switch <agent>" — the backend slash handler just flips the agent and
    // shows the pill (no reply), so the intent was never forwarded and the silent
    // trigger must deliver it. If the chip's value already carries the intent
    // (e.g. "/switch X book a meeting"), it doesn't match the bare-switch regex,
    // so the LLM router acts on it and replies directly — a silent-trigger replay
    // would then produce a DUPLICATE greeting. Skip it in that case.
    const isBareSwitch = /^\/switch\s+\S+$/i.test(msg.trim());
    this._pendingSilentTrigger = isBareSwitch;
    // Chip click = explicit agent pick. The /switch send itself is slash-
    // prefixed (deterministic server-side), so the pick stays armed for the
    // silent replay / first ordinary send that follows.
    if (chip.agent_id) this._pendingExplicitAgentPick = chip.agent_id;
    this._sendMessage(sid, msg);
  }

  get slashMenuItems(): SlashCommandItem[] {
    const items: SlashCommandItem[] = [];
    for (const agent of this.catalogAgents()) {
      if (!agent.command) continue;
      // Headline entry: drops just the command into the box so the user can
      // write (or paste) their own message after it.
      items.push({
        agentId: agent.agentId,
        agentName: agent.displayName,
        command: agent.command,
        label: 'Write your own message',
        prompt: '',
        insertOnly: true,
        searchText: `${agent.displayName} ${agent.command} ${agent.agentId}`.toLowerCase(),
      });
      for (const chat of agent.quickChats ?? []) {
        items.push({
          agentId: agent.agentId,
          agentName: agent.displayName,
          command: agent.command,
          label: chat.label,
          prompt: chat.prompt,
          insertOnly: false,
          searchText: `${agent.displayName} ${agent.command} ${chat.label} ${chat.prompt}`.toLowerCase(),
        });
      }
    }
    // Pinned hub entry. `/help` has always existed server-side (the hub's
    // built-in lists every command the caller can use), but the menu is
    // catalog-driven so nothing ever surfaced it — the hub's own
    // "unknown command" reply pointed users at a command they could not find.
    items.push(HELP_SLASH_ITEM);
    const q = this.slashQuery().toLowerCase();
    if (!q) return items;
    return items.filter(item =>
      item.searchText.includes(q)
      || item.command.toLowerCase().includes(`/${q}`),
    );
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (this.slashMenuOpen()) {
      const items = this.slashMenuItems;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (items.length) {
          this.slashHighlightIdx.update(i => Math.min(i + 1, items.length - 1));
          this._scrollSlashHighlightIntoView();
        }
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.slashHighlightIdx.update(i => Math.max(i - 1, 0));
        this._scrollSlashHighlightIntoView();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.slashMenuOpen.set(false);
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const idx = this.slashHighlightIdx();
        if (items[idx]) this.applySlashSelection(items[idx]);
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        if (items[0]) this.applySlashSelection(items[this.slashHighlightIdx()]);
        return;
      }
    }
    if (event.key === 'Escape' && this.isInFlight()) {
      event.preventDefault();
      this.onStop();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendMessage();
    }
  }

  onTextareaInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    const selStart = el.selectionStart;
    const selEnd = el.selectionEnd;
    this.currentInput.set(el.value);
    this._updateSlashMenu(el);
    this._resizeTextarea(el);
    // Preserve caret — [value] binding is intentionally omitted to avoid jumps.
    queueMicrotask(() => {
      if (document.activeElement !== el) return;
      const len = el.value.length;
      el.selectionStart = Math.min(selStart ?? len, len);
      el.selectionEnd = Math.min(selEnd ?? len, len);
    });
  }

  onSlashItemClick(index: number): void {
    const item = this.slashMenuItems[index];
    if (item) this.applySlashSelection(item);
  }

  applySlashSelection(item: SlashCommandItem): void {
    const text = item.prompt.trim()
      ? `${item.command} ${item.prompt}`.trim()
      : item.command;
    // Drop the selection into the box (with a trailing space) rather than
    // sending it — the user can edit it or add their own message, then submit.
    this._setInputValue(`${text} `);
    this.slashMenuOpen.set(false);
    this.slashQuery.set('');
    this.slashHighlightIdx.set(0);
    this._focusInputAtEnd();
    // Slash-menu pick = explicit agent choice (same one-shot fast-path
    // contract as a catalog pick).
    if (item.agentId) this._pendingExplicitAgentPick = item.agentId;
  }

  /** Refocus the chat textarea and place the caret at the end. */
  private _focusInputAtEnd(): void {
    queueMicrotask(() => {
      const el = this.chatTextarea?.nativeElement;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      el.selectionStart = el.selectionEnd = len;
    });
  }

  private _updateSlashMenu(el: HTMLTextAreaElement): void {
    const parsed = this._parseSlashContext(el.value, el.selectionStart ?? el.value.length);
    if (parsed) {
      const wasOpen = this.slashMenuOpen();
      const prevQuery = this.slashQuery();
      this.slashMenuOpen.set(true);
      this.slashQuery.set(parsed.query);
      if (!wasOpen || parsed.query !== prevQuery) {
        this.slashHighlightIdx.set(0);
      }
    } else {
      this.slashMenuOpen.set(false);
      this.slashQuery.set('');
      this.slashHighlightIdx.set(0);
    }
  }

  private _scrollSlashHighlightIntoView(): void {
    queueMicrotask(() => {
      document.querySelector('.ah-slash-item--active')?.scrollIntoView({ block: 'nearest' });
    });
  }

  private _resizeTextarea(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    // Cap matches `.ah-input { max-height }` — ~10 lines before the scrollbar.
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }

  /** Push a value into the signal and the live textarea (catalog picks, clears, etc.). */
  private _setInputValue(value: string): void {
    this.currentInput.set(value);
    const el = this.chatTextarea?.nativeElement;
    if (el) {
      el.value = value;
      this._resizeTextarea(el);
    }
  }

  private _clearInput(): void {
    this.currentInput.set('');
    const el = this.chatTextarea?.nativeElement;
    if (el) {
      el.value = '';
      el.style.height = 'auto';
    }
  }

  private _parseSlashContext(
    value: string,
    cursor: number,
  ): { query: string } | null {
    const before = value.slice(0, cursor);
    const lineStart = before.lastIndexOf('\n') + 1;
    const segment = before.slice(lineStart);
    const match = segment.match(/^\/([^\s]*)$/);
    if (!match) return null;
    return { query: match[1].toLowerCase() };
  }

  // ── WS event handler ──────────────────────────────────────────

  private _handleWsEvent(ev: Record<string, unknown>): void {
    // Turn-identity gate (see _activeTurnId / onStop). Frames without a
    // turn_id (older backends) pass through unchanged.
    const evTurn = typeof ev['turn_id'] === 'string' ? (ev['turn_id'] as string) : '';
    if (evTurn && this._stoppedTurnIds.has(evTurn)) {
      // Late frame from a turn the user stopped. Its `done` is the server's
      // close-out: the partial reply (or nothing, if a newer message
      // superseded it) is now persisted — refresh the list and forget the id.
      if (ev['type'] === 'done') {
        this._stoppedTurnIds.delete(evTurn);
        this._refreshSessions();
        this.usageSvc.refresh();
      }
      return;
    }
    if (evTurn && this._activeTurnId && evTurn !== this._activeTurnId) return;
    if (ev['type'] === 'cancel_ack') return;  // informational; the stopped turn's own done closes it

    switch (ev['type']) {
      case 'thinking':
        this._updateAgentMsg(m => ({ ...m, isThinking: true }));
        break;

      case 'history': {
        // Prior turns from a resumed session. Insert BEFORE the welcome
        // placeholder (which sits at _currentAgentMsgIdx) so the placeholder
        // stays the target for the trailing `text` welcome event.
        const hist = (ev['messages'] as Array<Record<string, unknown>> | undefined) ?? [];
        if (!hist.length) break;
        const placeholderIdx = this._currentAgentMsgIdx;
        const historyMsgs: ChatMessage[] = hist.map((h, hIdx) => {
          // The last history message is the most recent turn — i.e. the pending
          // step the user left off at. Its picker/card must stay INTERACTIVE
          // (it renders with isPast=false via $last), so we must NOT mark it
          // selected/confirmed below. Only past turns get locked into read-only.
          const isLastHistory = hIdx === hist.length - 1;
          const ui = h['ui'] as ChatMessage['ui'] | undefined;
          const msg: ChatMessage = {
            id: ++this._msgId,
            role: h['role'] === 'assistant' ? 'agent' : 'user',
            // Confirm/reject turns persist their wire token — show the human
            // label instead of a base64 blob.
            content: maskActionToken(String(h['content'] ?? '')),
            ...(ui ? { ui } : {}),
          };

          // Restore an agent-switch pill ("Switched to X"). The backend
          // (resume._window_to_display_messages) converts persisted switch
          // markers into a `_pill` so resume matches the live tool_call/
          // tool_result pill instead of showing the raw marker text.
          const pill = h['_pill'] as Record<string, unknown> | undefined;
          if (pill?.['name']) {
            msg.toolActivity = [{
              name: String(pill['name']),
              status: pill['status'] === 'error' ? 'error' as const : 'done' as const,
              startedAt: Date.now(),
            }];
          }

          // Restore wizard state from the per-message _wizard field embedded by
          // loop.py onto each agenthome conversation_window assistant message.
          // Each message carries its own wizard type + data so every turn in
          // history restores its card independently.
          const wizard = h['_wizard'] as Record<string, unknown> | undefined;
          if (wizard?.['type'] === 'pcheck_ui_events') {
            const events = (wizard['events'] as Array<Record<string, unknown>>) ?? [];
            for (const ev of events) {
              const evType = String(ev['type'] ?? '');
              if (evType === 'pcheck_ui') {
                this._applyPcheckUiFields(msg, ev);
              } else if (evType === 'booking_slots') {
                // Always restore the full slots array. The pcheck-renderer shows the
                // calendar for the last message (isPast=false, interactive) and for
                // past messages where bookingConfirmed=true (disabled, confirmed state).
                msg.bookingSlots = (ev['startTimes'] as ChiliPiperSlot[]) ?? [];
                msg.bookingRouteId = String(ev['routeId'] ?? '');
                msg.bookingExecEmail = String(ev['execEmail'] ?? '');
                msg.bookingExecName = String(ev['execName'] ?? '');
                msg.bookingDuration = String(ev['duration'] ?? '');
                msg.bookingFormData = (ev['formData'] as Record<string, string>) ?? {};
                if (ev['eventTzIana']) msg.bookingEventTzIana = String(ev['eventTzIana']);
                if (ev['eventTzLabel']) msg.bookingEventTzLabel = String(ev['eventTzLabel']);
              } else if (evType === 'booking_confirmed') {
                msg.bookingConfirmed = true;
                msg.bookingConfirmedTime = String(ev['startTime'] ?? '');
              } else if (evType === 'reschedule_slots') {
                // Mirror booking_slots: restore the full slots so the calendar
                // re-renders interactively on the last turn, or read-only/confirmed
                // on past turns.
                msg.rescheduleSlots = (ev['startTimes'] as ChiliPiperSlot[]) ?? [];
                msg.rescheduleSlotsCpMeetingId = String(ev['cpMeetingId'] ?? '');
                msg.rescheduleSlotsExecEmail = String(ev['execEmail'] ?? '');
                msg.rescheduleSlotsExecName = String(ev['execName'] ?? '');
                msg.rescheduleSlotsDuration = String(ev['duration'] ?? '');
                if (ev['currentStartTime']) msg.rescheduleSlotsCurrentStartTime = String(ev['currentStartTime']);
                if (ev['eventTzIana']) msg.rescheduleSlotsEventTzIana = String(ev['eventTzIana']);
                if (ev['eventTzLabel']) msg.rescheduleSlotsEventTzLabel = String(ev['eventTzLabel']);
              } else if (evType === 'reschedule_confirmed') {
                msg.rescheduleSlotsConfirmed = true;
                msg.rescheduleSlotsConfirmedTime = String(ev['startTime'] ?? '');
              }
            }
            // Lock PAST turns into read-only/disabled mode. The renderer's
            // [disabled] bindings key off these *Selected/*Confirmed flags, so
            // setting them on the LAST (active) message would wrongly disable the
            // pending picker the user needs to act on — guard with !isLastHistory.
            if (!isLastHistory) {
              if (msg.contactsList?.length) msg.contactsSelected = true;
              if (msg.executivesList?.length) msg.executivesSelected = true;
              if (msg.callObjectiveOptions?.length) msg.callObjectiveConfirmed = true;
              // Reschedule/cancel/add-participants cards are data-driven (shown
              // when their *Details exist) and aren't isPast-gated, so on resume
              // they'd otherwise appear actionable. Mark them confirmed so the
              // restored card renders read-only.
              if (msg.rescheduleDetails) msg.rescheduleConfirmed = true;
              if (msg.cancelDetails) msg.cancelConfirmed = true;
              if (msg.addParticipantsDetails) msg.addParticipantsConfirmed = true;
            }
            // Stamp ui.component so _msgRenderer always returns 'pcheck' for
            // these messages, regardless of the session's current activeAgentId.
            // The server's _window_to_display_messages only passes _wizard through
            // (not ui), so without this stamp, mixed-agent sessions where the last
            // active agent is not PCHECK would render all PCHECK history turns as
            // plain text, making wizards invisible / distorted on resume.
            if (!msg.ui?.component) msg.ui = { component: 'pcheck' };
          } else if (wizard?.['type'] === 'summary') {
            msg.summaryAvailable = true;
            if (wizard['report_url']) msg.sfReportUrl = String(wizard['report_url']);
            if (!msg.ui?.component) msg.ui = { component: 'summary' };
          } else if (wizard?.['type'] === 'artifact') {
            // NEAT report card. Rebuild the artifact from the embedded event
            // (mirrors the live `artifact` WS handler) so the card reappears on
            // resume; the report opens from its URL in the side panel on click.
            const ev = (wizard['event'] as Record<string, unknown>) ?? {};
            const fileUrl = ev['file_url'] ? String(ev['file_url']) : undefined;
            msg.artifact = {
              id: `${this.sessionId() ?? 's'}:${msg.id}`,
              type: ev['artifact_type'] ? String(ev['artifact_type']) : (fileUrl ? 'document' : 'markdown'),
              title: ev['title'] ? String(ev['title']) : 'Report',
              icon: ev['icon'] ? String(ev['icon']) : undefined,
              // Prefer the agent stamped on the persisted event. On resume the
              // card is rebuilt before activeAgentId is set (it lands on `done`),
              // so the signal is null here — that dropped the Reanalyze button.
              agentId: ev['agent_id'] ? String(ev['agent_id']) : (this.activeAgentId() ?? undefined),
              body: ev['body'] != null ? String(ev['body']) : undefined,
              fileUrl,
              fileName: ev['file_name'] ? String(ev['file_name']) : undefined,
              uploadFilename: ev['source_filename'] ? String(ev['source_filename']) : undefined,
            };
          } else if (wizard?.['type'] === 'downloads') {
            // AI_ROLE_TRANSFORM document downloads embedded by loop.py. Rebuild
            // the download buttons (mirrors the live `download_ready` handler) so
            // they reappear when a Role Transform chat is reopened from history.
            const items = (wizard['items'] as Array<Record<string, unknown>>) ?? [];
            msg.downloads = items.map(it => ({
              filename: String(it['filename'] ?? ''),
              url:      String(it['url'] ?? ''),
              docType:  String(it['doc_type'] ?? ''),
            }));
            // Stamp the renderer so these turns use the AI-role renderer (which
            // owns the download buttons) regardless of the session's current agent.
            if (!msg.ui?.component) msg.ui = { component: 'airole' };
          } else if (wizard?.['type'] === 'licext_form') {
            // License Extension inline wizard. Restore the form (or the submit
            // completion card) embedded by loop.py so the turn reappears on
            // resume. PAST turns render DISABLED — the renderer's
            // `locked = isPast || submitted` disables every non-last turn — while
            // the LAST message stays ENABLED so an unsubmitted, in-progress form
            // can be continued when the user returns to the chat.
            if (wizard['flow_complete']) {
              msg.licextFlowComplete = wizard['flow_complete'] as LicextFlowComplete;
            } else if (wizard['form_data']) {
              msg.licextForm = wizard['form_data'] as LicextFormData;
              msg.licextFormSubmitted = false;
            }
            // Stamp the renderer so mixed-agent sessions still route these turns
            // to the default branch that hosts the licext renderer (otherwise a
            // session whose last agent is pcheck/airole would hide the form).
            if (!msg.ui?.component) msg.ui = { component: 'licext' };
          } else if (ui?.component === 'summary') {
            // Fallback for sessions created before _wizard was introduced.
            msg.summaryAvailable = true;
            if (ui.props?.['sfReportUrl']) msg.sfReportUrl = String(ui.props['sfReportUrl']);
          }

          // Rebuild the report-panel artifact from the restored fields so old
          // reports re-open from their card. NEAT artifacts are restored above
          // from the embedded `artifact` _wizard, so don't clobber them here —
          // _makeArtifact only handles the SF-summary / prep-doc shapes.
          msg.artifact = msg.artifact ?? this._makeArtifact(msg, !!msg.summaryAvailable);

          return msg;
        });

        // Prep-doc card dedupe. The PCHECK flow emits a `get_prep_doc` UI event
        // (→ reportUrl) on TWO turns: confirm_call_objective ("Generating your
        // prep doc now…") and the later get_prep_doc ("your prep doc is ready").
        // Live, the objective turn's card is suppressed by [PULSE_CHECK_COMPLETE]
        // (isPulseCheckComplete), which isn't reconstructed on resume — so both
        // restored messages render a "Prep Doc" card. Keep the card only on the
        // LAST prep-doc turn (the ready one, mirroring live) and drop it from
        // earlier ones so a resumed session shows a single card.
        const prepDocIdxs = historyMsgs
          .map((m, i) => (m.reportUrl ? i : -1))
          .filter(i => i >= 0);
        for (const i of prepDocIdxs.slice(0, -1)) {
          historyMsgs[i] = { ...historyMsgs[i], artifact: undefined };
        }

        this.messages.update(msgs => {
          if (placeholderIdx >= 0 && placeholderIdx < msgs.length) {
            return [
              ...msgs.slice(0, placeholderIdx),
              ...historyMsgs,
              ...msgs.slice(placeholderIdx),
            ];
          }
          return [...msgs, ...historyMsgs];
        });
        if (placeholderIdx >= 0) {
          this._currentAgentMsgIdx = placeholderIdx + historyMsgs.length;
        }
        break;
      }

      // ── Live "Thinking" block (flag-gated reasoning stream) ─────────────
      // `reasoning_delta` streams while the model thinks; `reasoning` is the
      // buffered variant (at most one per turn, replaces the block content).
      // EPHEMERAL: the `history` resume handler has no reasoning fields, so a
      // reopened session simply renders without the block — by design.
      case 'reasoning_delta': {
        if (this._isResumingSession) break;
        const reasoningDelta = String(ev['content'] ?? '');
        if (!reasoningDelta) break;
        this._updateAgentMsg(m => ({
          ...m,
          reasoning: (m.reasoning ?? '') + reasoningDelta,
          // Auto-open on the FIRST frame only — later deltas must not fight a
          // manual collapse while the model is still thinking.
          reasoningOpen: m.reasoning ? m.reasoningOpen : true,
        }));
        break;
      }

      case 'reasoning': {
        if (this._isResumingSession) break;
        const reasoningFull = String(ev['content'] ?? '');
        if (!reasoningFull) break;
        this._updateAgentMsg(m => ({
          ...m,
          reasoning: reasoningFull,
          reasoningOpen: m.reasoning ? m.reasoningOpen : true,
        }));
        break;
      }

      case 'text_delta': {
        if (this._isResumingSession) break;
        const deltaContent = String(ev['content'] ?? '');
        const deltaSummaryAvailable = Boolean(ev['summary_available']);
        const deltaReportUrl = ev['report_url'] ? String(ev['report_url']) : undefined;
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          content: m.content + deltaContent,
          // First answer text of the turn — auto-collapse (not remove) the
          // Thinking block. One-shot: a manual re-expand stays expanded.
          ...(deltaContent && m.reasoning && !m.reasoningAutoCollapsed
            ? { reasoningOpen: false, reasoningAutoCollapsed: true } : {}),
          ...(deltaSummaryAvailable ? { summaryAvailable: true } : {}),
          ...(deltaReportUrl ? { sfReportUrl: deltaReportUrl } : {}),
        }));
        if (deltaContent) {
          this._resolveStuckPills();
          this.stillWorking.set(false);  // reply text is visible — drop the escalated hint
        }
        if (deltaSummaryAvailable) this._surfaceSummaryArtifact();
        break;
      }

      case 'text': {
        if (this._isResumingSession) break;
        const rawContent = String(ev['content'] ?? '');
        const isPulseCheckComplete = rawContent.includes(PULSE_CHECK_COMPLETE_MARKER);
        const cleanContent = rawContent.replace(PULSE_CHECK_COMPLETE_MARKER, '').trim();
        const summaryAvailable = Boolean(ev['summary_available']);
        const reportUrl = ev['report_url'] ? String(ev['report_url']) : undefined;
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          content: cleanContent,
          // Answer arrived — auto-collapse the Thinking block (see text_delta).
          ...(cleanContent && m.reasoning && !m.reasoningAutoCollapsed
            ? { reasoningOpen: false, reasoningAutoCollapsed: true } : {}),
          ...(summaryAvailable ? { summaryAvailable: true } : {}),
          ...(reportUrl ? { sfReportUrl: reportUrl } : {}),
          ...(isPulseCheckComplete ? { isPulseCheckComplete: true } : {}),
        }));
        if (cleanContent) {
          this._resolveStuckPills();
          this.stillWorking.set(false);  // reply text is visible — drop the escalated hint
        }
        if (summaryAvailable) this._surfaceSummaryArtifact();
        break;
      }

      case 'sf_report': {
        const reportUrl = ev['report_url'] ? String(ev['report_url']) : undefined;
        if (reportUrl) {
          this._updateAgentMsg(m => ({ ...m, sfReportUrl: reportUrl, summaryAvailable: true }));
          this._surfaceSummaryArtifact();
        }
        break;
      }

      case 'suggestions': {
        const opts = (ev['options'] as SuggestionChip[] | undefined) ?? [];
        this._updateAgentMsg(m => ({ ...m, isThinking: false, suggestions: opts }));
        break;
      }

      // ── Confirm-before-write: the runtime proposed write actions; nothing
      // executes until the user answers via the card. A new proposal
      // supersedes any older unanswered one (its token would re-propose
      // anyway, but the UI shouldn't offer two live cards).
      case 'action_proposal': {
        const actions = (ev['actions'] as ActionProposal['actions'] | undefined) ?? [];
        const confirmMessage = String(ev['confirm_message'] ?? '');
        const rejectMessage = String(ev['reject_message'] ?? '');
        if (!actions.length || !confirmMessage || !rejectMessage) break;
        this.messages.update(msgs => msgs.map(m =>
          m.actionProposal && !m.actionProposalResolved
            ? { ...m, actionProposalResolved: 'superseded' as const } : m));
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          actionProposal: {
            title: String(ev['title'] ?? 'Confirm before writing'),
            actions, confirmMessage, rejectMessage,
          },
        }));
        break;
      }

      // Outcomes arrive alongside a `text` event that narrates them; the card
      // is already locked at click time, so nothing further to render here.
      case 'action_result':
        break;

      // Per-user OAuth connectors: a tool needed the caller's own third-party
      // access and they haven't connected that service. Rendered as a card
      // that opens the Connections modal. (Forward-compatible: the runtime emission
      // ships with the connector rollout; the model's text also narrates it.)
      case 'connection_required': {
        const connector = String(ev['connector'] ?? '');
        if (!connector) break;
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          connectionRequired: {
            connector,
            message: String(ev['message'] ?? ''),
          },
        }));
        break;
      }

      case 'renderer_hint':
        this._updateAgentMsg(m => ({
          ...m, rendererHint: String(ev['key'] ?? ''),
        }));
        break;

      // ── Generic report artifact (NEAT today; any agent later) ───────────────
      // The runtime emits this when a report is ready. The UI maps it to an
      // Artifact, attaches it to the current assistant message (so the chat shows
      // a card instead of the inline report), and auto-opens the panel.
      case 'artifact': {
        const sid = this.sessionId() ?? 's';
        const idx = this._currentAgentMsgIdx;
        const msgId = idx >= 0 ? this.messages()[idx]?.id : this._msgId;
        const fileUrl = ev['file_url'] ? String(ev['file_url']) : undefined;
        const artifact: Artifact = {
          id: `${sid}:${msgId}`,
          // A URL-addressable report renders in an iframe (looks like the link);
          // a body-only report falls back to markdown.
          type: ev['artifact_type'] ? String(ev['artifact_type']) : (fileUrl ? 'document' : 'markdown'),
          title: ev['title'] ? String(ev['title']) : 'Report',
          icon: ev['icon'] ? String(ev['icon']) : undefined,
          // Prefer the agent stamped on the event so the card is self-describing
          // and survives a resume (where activeAgentId isn't set until `done`).
          agentId: ev['agent_id'] ? String(ev['agent_id']) : (this.activeAgentId() ?? undefined),
          body: ev['body'] != null ? String(ev['body']) : undefined,
          fileUrl,
          fileName: ev['file_name'] ? String(ev['file_name']) : undefined,
          uploadFilename: ev['source_filename'] ? String(ev['source_filename']) : undefined,
        };
        this._updateAgentMsg(m => ({ ...m, isThinking: false, artifact }));
        this._consumePendingAttachment();  // a report was produced from the upload
        if (!this._isResumingSession) this.artifacts.open(artifact);
        break;
      }

      // ── Tool activity (live "Searching Salesforce…" pills) ──────────────
      // Emitted by the agent loop (run_agent_sync) and relayed PII-free by
      // agenthome dispatch. Rendered by app-tool-activity-pills.
      //
      // Supervisor housekeeping (persisting session metadata) stays hidden via
      // HIDDEN_ACTIVITY_TOOLS. dispatch_to_agent DOES render ("Routing to
      // agent…") — it covers the longest silent stretch of a routed turn.
      case 'tool_call': {
        const name = String(ev['name'] ?? '');
        if (name && !HIDDEN_ACTIVITY_TOOLS.has(name)) {
          this._updateAgentMsg(m => ({
            ...m, isThinking: false,
            toolActivity: [...(m.toolActivity ?? []),
              { name, status: 'running' as const, startedAt: Date.now() }],
          }));
        }
        break;
      }

      case 'tool_result': {
        const name = String(ev['name'] ?? '');
        const status = ev['status'] === 'error' ? 'error' as const : 'done' as const;
        // Optional overrides: `label` replaces the pill's displayed text on resolve
        // (e.g. a "Switched to X" running pill flips to "Not Available" when the
        // target agent declines/isn't available); `error` shows a short sub-label.
        const overrideLabel = ev['label'] != null ? String(ev['label']) : undefined;
        const errText = ev['error'] != null ? String(ev['error']) : undefined;
        // Hold the "running" pill for a minimum time so fast tools don't flash
        // straight to "complete" — the in-progress state stays perceptible.
        const idx = this._currentAgentMsgIdx;
        const msg = idx >= 0 ? this.messages()[idx] : undefined;
        const msgId = msg?.id;
        const running = msg?.toolActivity?.slice().reverse()
          .find(a => a.name === name && a.status === 'running');
        const elapsed = running?.startedAt ? Date.now() - running.startedAt : PILL_MIN_VISIBLE_MS;
        const flip = () => this._updateMsgById(msgId, m => {
          const acts = [...(m.toolActivity ?? [])];
          for (let i = acts.length - 1; i >= 0; i--) {
            if (acts[i].name === name && acts[i].status === 'running') {
              acts[i] = {
                ...acts[i],
                status,
                ...(overrideLabel ? { name: overrideLabel } : {}),
                ...(errText ? { error: errText } : {}),
              };
              break;
            }
          }
          return { ...m, toolActivity: acts };
        });
        if (elapsed >= PILL_MIN_VISIBLE_MS) flip();
        else setTimeout(flip, PILL_MIN_VISIBLE_MS - elapsed);
        break;
      }

      // ── AI_ROLE_TRANSFORM document download events ──────────────────────
      case 'download_ready': {
        const item: DownloadItem = {
          filename: String(ev['filename'] ?? ''),
          url:      String(ev['url'] ?? ''),
          docType:  String(ev['doc_type'] ?? ''),
        };
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          downloads: [...(m.downloads ?? []), item],
        }));
        this._consumePendingAttachment();  // a document was produced from the upload
        break;
      }

      // ── IMPACT_PCHECK interactive events (relayed by agenthome dispatch) ──
      case 'pcheck_ui':
        this._applyPcheckUi(ev);
        break;

      // NEAT agent-driven in-chat deal-context wizard. The runtime emits this
      // after reading an uploaded transcript; we flag the current message so the
      // template renders <app-neat-deal-context> inline in the thread.
      case 'neat_ui':
        if (String(ev['name'] ?? '') === 'deal_context') {
          this._updateAgentMsg(m => ({ ...m, isThinking: false, neatDealContext: true }));
          this._consumePendingAttachment();  // NEAT received the file → stop re-sending it
        }
        break;

      // ── LICEXTAGENT inline wizard events ──
      case 'licext_form':
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          licextForm: ev['form_data'] as LicextFormData,
          licextFormSubmitted: false,
        }));
        break;

      case 'licext_flow_complete':
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          licextFlowComplete: {
            flow_type: (ev['flow_type'] === 'saas' ? 'saas' : 'device'),
            request_id: String(ev['request_id'] ?? ''),
            direct_count: Number(ev['direct_count'] ?? 0),
            pending_count: Number(ev['pending_count'] ?? 0),
          } as LicextFlowComplete,
        }));
        break;

      case 'booking_slots':
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          bookingSlots: (ev['startTimes'] as ChiliPiperSlot[]) ?? [],
          bookingRouteId: String(ev['routeId'] ?? ''),
          bookingExecEmail: String(ev['execEmail'] ?? ''),
          bookingExecName: String(ev['execName'] ?? ''),
          bookingDuration: String(ev['duration'] ?? ''),
          bookingFormData: (ev['formData'] as Record<string, string>) ?? {},
          bookingEventTzIana: ev['eventTzIana'] ? String(ev['eventTzIana']) : undefined,
          bookingEventTzLabel: ev['eventTzLabel'] ? String(ev['eventTzLabel']) : undefined,
        }));
        break;

      case 'booking_confirmed':
        // This meeting's intake is done — clear the running guest-picker cache
        // (see _additionalGuests/_selectedContactEmail declarations) so the
        // NEXT booking's guest wizard opens empty instead of pre-populated
        // with this meeting's guests/contact.
        this._additionalGuests = [];
        this._selectedContactEmail = '';
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          bookingConfirmed: true,
          bookingConfirmedTime: String(ev['startTime'] ?? ''),
        }));
        break;

      case 'reschedule_slots':
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          rescheduleSlots: (ev['startTimes'] as ChiliPiperSlot[]) ?? [],
          rescheduleSlotsCpMeetingId: String(ev['cpMeetingId'] ?? ''),
          rescheduleSlotsExecEmail: String(ev['execEmail'] ?? ''),
          rescheduleSlotsExecName: String(ev['execName'] ?? ''),
          rescheduleSlotsDuration: String(ev['duration'] ?? ''),
          rescheduleSlotsCurrentStartTime: ev['currentStartTime'] ? String(ev['currentStartTime']) : undefined,
          rescheduleSlotsEventTzIana: ev['eventTzIana'] ? String(ev['eventTzIana']) : undefined,
          rescheduleSlotsEventTzLabel: ev['eventTzLabel'] ? String(ev['eventTzLabel']) : undefined,
        }));
        break;

      case 'reschedule_confirmed':
        this._updateAgentMsg(m => ({
          ...m, isThinking: false,
          rescheduleSlotsConfirmed: true,
          rescheduleSlotsConfirmedTime: String(ev['startTime'] ?? ''),
        }));
        break;

      case 'done': {
        // Resume completed, but the turn the user left mid-flight hadn't
        // finished server-side yet (see loop.py's turn_status field / the
        // abandonment-recovery fix in handle_turn). This is NOT the turn
        // completing — don't finalize the UI as if it were: keep isInFlight,
        // surface a distinct "still working" hint, and poll again shortly.
        // active_agent_id/state ARE authoritative already (the switch itself
        // did complete), so apply those now for a correct header immediately.
        if (this._isResumingSession && ev['turn_status'] === 'running') {
          this.resumePending.set(true);
          if (ev['active_agent_id'] != null) {
            this.activeAgentId.set(String(ev['active_agent_id']));
            this.agentState.set('IN_AGENT_MODE');
          }
          const sid = this.sessionId();
          if (sid) this._scheduleResumePoll(sid);
          break;
        }
        // The abandoned turn errored server-side (handle_turn's error path
        // deliberately does not persist a reply — see the backend's
        // turn_status contract). Falling through to the normal finalize below
        // would leave an empty agent bubble with no explanation; say so instead.
        if (this._isResumingSession && ev['turn_status'] === 'error') {
          this.resumePending.set(false);
          this._isResumingSession = false;
          this.isInFlight.set(false);
          this._updateAgentMsg(m => ({
            ...m, isThinking: false,
            content: 'Something went wrong processing your last message. Please try again.',
          }));
          this._currentAgentMsgIdx = -1;
          if (ev['active_agent_id'] != null) {
            this.activeAgentId.set(String(ev['active_agent_id']));
            this.agentState.set('IN_AGENT_MODE');
          } else {
            this.activeAgentId.set(null);
            this.agentState.set('HOME');
          }
          this._refreshSessions();
          break;
        }
        this.resumePending.set(false);
        // Closed by a Stop that didn't come through this component (another
        // tab, or the server superseding it): mark the bubble like onStop does.
        if (ev['stopped'] === true) {
          this._updateAgentMsg(m => ({ ...m, isThinking: false, isStopped: true }));
        }
        this.usageSvc.refresh();  // a turn completed — refresh the usage widget
        this.statsSvc.refresh();  // a completed turn may have qualified a new session
        // Captured before the resume block clears the flag — gates auto-open so
        // replaying a resumed session never pops the panel open on its own.
        const wasResuming = this._isResumingSession;
        // Resume turn completed — remove the placeholder agent message that was
        // added to keep _currentAgentMsgIdx valid. The "Welcome back" text was
        // already suppressed in the text/text_delta handlers above.
        if (this._isResumingSession) {
          const phIdx = this._currentAgentMsgIdx;
          if (phIdx >= 0) {
            this.messages.update(msgs => msgs.filter((_, i) => i !== phIdx));
          }
          this._currentAgentMsgIdx = -1;
          this._isResumingSession = false;
          // Still let the rest of done run so activeAgentId is set correctly.
        }

        const nextAgentId = ev['active_agent_id'] != null ? String(ev['active_agent_id']) : null;
        const isPcheckTurn =
          this.isPcheckAgent(this.activeAgentId()) || this.isPcheckAgent(nextAgentId);
        let triggerPrepDoc = false;
        // Resolve any pill still "running" (agents that emit tool_call but not
        // a matching tool_result) so it doesn't spin forever. Also strip the
        // completion marker from the final text so it never renders to the user.
        const doneSummaryAvailable = Boolean(ev['summary_available']);
        // Index of the agent message that just finished — captured before the
        // reset below so we can inspect its suggestion chips when deciding
        // whether a still-staged upload should be dropped (see end of this case).
        const completedAgentMsgIdx = this._currentAgentMsgIdx;
        // Artifact built this turn (SF summary / prep doc), captured for auto-open.
        let createdArtifact: Artifact | undefined;
        this._updateAgentMsg(m => {
          const hasPulseComplete = m.content.includes(PULSE_CHECK_COMPLETE_MARKER);
          const cleanContent = hasPulseComplete
            ? m.content.replace(PULSE_CHECK_COMPLETE_MARKER, '').trim()
            : m.content;
          if (isPcheckTurn && (hasPulseComplete || m.isPulseCheckComplete) && !this._prepDocAutoTriggered) {
            triggerPrepDoc = true;
          }
          const finalSummaryAvailable = m.summaryAvailable || doneSummaryAvailable;
          // Resolve the renderer for this specific turn using nextAgentId (what
          // activeAgentId will be set to) so mixed-agent sessions round-trip correctly.
          const uiComponent: 'pcheck' | 'airole' | 'summary' | 'plain' = finalSummaryAvailable ? 'summary'
            : nextAgentId && PCHECK_AGENT_IDS.includes(nextAgentId) ? 'pcheck'
            : nextAgentId && AI_ROLE_AGENT_IDS.includes(nextAgentId) ? 'airole'
            : 'plain';
          // Derive the report-panel artifact from the per-agent fields (NEAT uses
          // its own `artifact` event, so it's already set and preserved here).
          createdArtifact = this._makeArtifact({ ...m, content: cleanContent }, finalSummaryAvailable);
          const artifact = createdArtifact ?? m.artifact;
          return {
            ...m, isThinking: false,
            content: cleanContent,
            // Turn over — a Thinking block left open by a text-less turn
            // collapses now (never removed; it stays expandable).
            ...(m.reasoning && !m.reasoningAutoCollapsed
              ? { reasoningOpen: false, reasoningAutoCollapsed: true } : {}),
            toolActivity: m.toolActivity?.map(a =>
              a.status === 'running' ? { ...a, status: 'done' as const } : a),
            isPulseCheckComplete: m.isPulseCheckComplete || hasPulseComplete,
            summaryAvailable: finalSummaryAvailable,
            // Ensure the summary card starts expanded once streaming completes.
            isCollapsed: finalSummaryAvailable ? false : m.isCollapsed,
            artifact,
            ui: {
              component: uiComponent,
              agentId: nextAgentId ?? undefined,
              ...(m.sfReportUrl ? { props: { sfReportUrl: m.sfReportUrl } } : {}),
            },
          };
        });
        // Auto-open the panel the first time a report is generated (not on resume).
        if (createdArtifact && !wasResuming) {
          this.artifacts.open(createdArtifact);
        }
        this.isInFlight.set(false);
        this._currentAgentMsgIdx = -1;
        const prevAgentId = this.activeAgentId();
        if (ev['active_agent_id'] != null) {
          this.activeAgentId.set(String(ev['active_agent_id']));
          this.agentState.set('IN_AGENT_MODE');
          // When switching into a PCheck agent from a non-PCheck context, silently
          // trigger the agent so it opens with its greeting + account ID prompt
          // without requiring the user to send a message first.
          const justSwitchedToPcheck =
            PCHECK_AGENT_IDS.includes(nextAgentId!) &&
            (!prevAgentId || !PCHECK_AGENT_IDS.includes(prevAgentId));
          if (justSwitchedToPcheck && this._pendingSilentTrigger && !wasResuming) {
            this._pendingSilentTrigger = false;
            setTimeout(() => {
              const sid = this.sessionId();
              if (sid) this._silentTrigger(sid);
            }, 300);
          } else {
            this._pendingSilentTrigger = false;
          }
        } else {
          this.activeAgentId.set(null);
          this.agentState.set('HOME');
        }
        // Drop a still-staged upload that the router did not keep in play this
        // turn. The chip is deliberately preserved across a router *clarification*
        // (see _composeAndRender) so the follow-up re-sends upload_filename, which
        // is per-turn only and not persisted server-side. The backend now sends an
        // authoritative `keep_upload` flag — true only when it offered a routing
        // choice with the file still un-dispatched; false once a downstream agent
        // received the file (consumed) or the router gave a terminal reply (an
        // out-of-scope decline / plain answer that will never use it). Without
        // dropping it, the chip lingers and silently re-attaches to every later
        // message. Fall back to the old chip/agent-switch heuristic only when an
        // older backend omits the flag (deploy skew).
        if (this.pendingAttachment()) {
          const completedAgentMsg =
            completedAgentMsgIdx >= 0 ? this.messages()[completedAgentMsgIdx] : undefined;
          const keepUpload =
            typeof ev['keep_upload'] === 'boolean'
              ? ev['keep_upload'] === true
              : nextAgentId != null || !!completedAgentMsg?.suggestions?.length;
          if (!keepUpload) {
            this.pendingAttachment.set(null);
          }
        }
        this._refreshSessions();
        // Flush a widget action the user triggered while the turn was still
        // streaming (e.g. picked a slot before `done` arrived). Send it now that
        // the previous turn has completed.
        if (this._pendingActionText) {
          const pending = this._pendingActionText;
          this._pendingActionText = null;
          const sid = this.sessionId();
          if (sid) this._sendMessage(sid, pending);
        }
        // Auto-trigger prep doc generation after the booking + call-objective
        // flow completes (fires at most once per session). Sent directly —
        // routing it through the input box (the old _setInputValue +
        // onSendMessage pair) would clobber a draft the user type-ahead
        // composed while the turn streamed.
        if (triggerPrepDoc) {
          this._prepDocAutoTriggered = true;
          setTimeout(() => {
            const prepSid = this.sessionId();
            if (prepSid && !this.isInFlight() && !this.usageBlocked()) {
              this._sendMessage(prepSid, 'Please generate the prep doc');
            }
          }, 500);
        }
        break;
      }

      case 'error':
        this._updateAgentMsg(m => ({
          ...m, isThinking: false, content: `Error: ${ev['message'] ?? 'unknown'}`,
        }));
        this.isInFlight.set(false);
        this._currentAgentMsgIdx = -1;
        // Drop any queued widget action — the turn failed, so don't auto-send
        // into a broken session.
        this._pendingActionText = null;
        break;

      case 'quota_exceeded': {
        const scope = String(ev['scope'] ?? 'monthly');
        const base = ev['message'] ? String(ev['message']) : `You've reached your ${scope} token limit.`;
        let when = '';
        try { const r = String(ev['resets_at'] ?? ''); if (r) when = new Date(r).toLocaleString(); } catch { /* ignore */ }
        this._updateAgentMsg(m => ({
          ...m, isThinking: false, content: when ? `${base} It resets ${when}.` : base,
        }));
        // A blocked turn never runs the model, so it won't get the normal
        // post-turn refresh — refresh the usage widget now to reflect the limit.
        this.usageSvc.refresh();
        this.isInFlight.set(false);
        this._currentAgentMsgIdx = -1;
        this._pendingActionText = null;
        break;
      }
    }
    this._scrollToBottom();
  }

  private _updateAgentMsg(fn: (m: ChatMessage) => ChatMessage): void {
    if (this._currentAgentMsgIdx < 0) return;
    this.messages.update(msgs => {
      const copy = [...msgs];
      copy[this._currentAgentMsgIdx] = fn(copy[this._currentAgentMsgIdx]);
      return copy;
    });
  }

  /** Update a specific message by id (used by deferred timers like the pill
   *  min-display flip, where _currentAgentMsgIdx may have moved on by the time
   *  the timeout fires). No-op if the id is missing. */
  private _updateMsgById(id: number | undefined, fn: (m: ChatMessage) => ChatMessage): void {
    if (id == null) return;
    this.messages.update(msgs => msgs.map(m => (m.id === id ? fn(m) : m)));
  }

  /** Toggle the live Thinking block expanded/collapsed by message index. */
  toggleReasoning(index: number): void {
    this.messages.update(msgs => {
      const copy = [...msgs];
      copy[index] = { ...copy[index], reasoningOpen: !copy[index].reasoningOpen };
      return copy;
    });
  }

  /** Toggle the SF account summary card collapsed/expanded state by message index. */
  toggleSfSummaryCollapse(index: number): void {
    this.messages.update(msgs => {
      const copy = [...msgs];
      copy[index] = { ...copy[index], isCollapsed: !copy[index].isCollapsed };
      return copy;
    });
  }

  /** Apply a single pcheck_ui event directly onto a ChatMessage object.
   *  Used for history restore (mutates msg in-place, no _selectedContactEmail
   *  filter since that state is not persisted across sessions). */
  private _applyPcheckUiFields(msg: ChatMessage, ev: Record<string, unknown>): void {
    const name = String(ev['name'] ?? '');
    switch (name) {
      case 'get_sf_contacts':
        msg.contactsList = (ev['contacts'] as ContactItem[]) ?? [];
        break;
      case 'select_executives':
        msg.executivesList = (ev['executives'] as ExecutiveItem[]) ?? [];
        msg.allExecutivesList = (ev['all_executives'] as ExecutiveItem[]) ?? [];
        // Impact sends the event zone so every tile renders in it.
        if (ev['eventTzIana']) msg.executivesEventTzIana = String(ev['eventTzIana']);
        if (ev['eventTzLabel']) msg.executivesEventTzLabel = String(ev['eventTzLabel']);
        break;
      case 'get_additional_guest_options':
        msg.additionalGuestContacts = (ev['additional_guest_contacts'] as ContactItem[]) ?? [];
        break;
      case 'generate_call_objective_options':
        msg.callObjectiveOptions = (ev['call_objective_options'] as string[]) ?? [];
        break;
      case 'get_prep_doc':
        msg.reportUrl = String(ev['report_url'] ?? '');
        break;
      case 'multiple_meetings_found':
        msg.multipleMeetings = (ev['meetings_list'] as MeetingListItem[]) ?? [];
        msg.multipleMeetingsFlow = String(ev['flow'] ?? '');
        msg.meetingPicked = false;
        if (ev['eventTzIana']) msg.multipleMeetingsEventTzIana = String(ev['eventTzIana']);
        break;
      case 'get_reschedule_link':
        msg.rescheduleUrl = String(ev['reschedule_url'] ?? '');
        msg.rescheduleDetails = ev['reschedule_details'] as RescheduleDetails;
        break;
      case 'get_cancel_details':
        msg.cancelDetails = ev['cancel_details'] as CancelDetails;
        break;
      case 'get_meeting_for_add_participants':
        msg.addParticipantsDetails = ev['add_participants_details'] as AddParticipantsDetails;
        if (ev['additional_guest_contacts'] !== undefined)
          msg.additionalGuestContacts = (ev['additional_guest_contacts'] as ContactItem[]) ?? [];
        break;
      case 'confirm_add_participants':
        msg.addParticipantsAdded = (ev['added_participants'] as string[]) ?? [];
        msg.addParticipantsConfirmed = true;
        break;
    }
  }

  /** Map a pcheck_ui event onto the current agent message's renderer fields.
   *  Field names mirror wg-agents-runtimes impact_pcheck tools.py (UI contract). */
  private _applyPcheckUi(ev: Record<string, unknown>): void {
    const name = String(ev['name'] ?? '');
    this._updateAgentMsg(m => {
      const next: ChatMessage = { ...m, isThinking: false };
      this._applyPcheckUiFields(next, ev);
      // Live session: filter additional guest contacts by primary contact email.
      if ((name === 'get_additional_guest_options' || name === 'get_meeting_for_add_participants')
          && this._selectedContactEmail) {
        const primary = this._selectedContactEmail;
        if (next.additionalGuestContacts) {
          next.additionalGuestContacts = next.additionalGuestContacts
            .filter(c => c.email.toLowerCase() !== primary);
        }
      }
      // Re-opened booking guest picker: pre-fill it with the guests already added
      // this flow so the user edits the running list instead of starting over.
      if (name === 'get_additional_guest_options' && this._additionalGuests.length) {
        next.additionalGuestsAdded = this._additionalGuests;
      }
      return next;
    });
  }

  /** Handle a user interaction from the PCheckRenderer: translate to the chat
   *  message the role prompt expects, optimistically lock the widget, and send. */
  /** LICEXTAGENT wizard submit: the renderer composed the seed message; mark the
   *  form submitted (so it stops rendering) and send it through the normal chat
   *  pipeline. */
  onLicextAction(action: LicextAction): void {
    const sid = this.sessionId();
    if (!sid) return;
    if (action.type === 'submit') {
      this._patchMsg(action.msgIdx, { licextFormSubmitted: true });
      this._sendMessage(sid, String(action.payload ?? ''));
    }
  }

  onPcheckAction(action: PCheckAction): void {
    const sid = this.sessionId();
    if (!sid) return;

    const p = action.payload;
    const lock = (patch: Partial<ChatMessage>) => this._patchMsg(action.msgIdx, patch);
    let text = '';

    switch (action.type) {
      case 'send-message':
        text = String(p ?? '');
        break;
      case 'contact-selected': {
        const c = p as ContactItem;
        text = `I select the contact: ${c.first_name} ${c.last_name}, email: ${c.email}`;
        this._selectedContactEmail = c.email.toLowerCase();
        lock({ contactsSelected: true });
        break;
      }
      case 'executive-selected': {
        const e = p as ExecutiveItem;
        // Include the email as a stable disambiguation key (name alone can collide).
        text = `I'd like to book with ${e.name}, email ${e.email}.`;
        lock({ executivesSelected: true });
        break;
      }
      case 'slot-selected': {
        // PCheckRenderer forwards the booking-calendar SlotSelectedEvent
        // ({ slot, routeId, execEmail, formData, timezone }); the ISO time is slot.startTime.
        const s = p as { slot?: ChiliPiperSlot; timezone?: string };
        const tz = s?.timezone ? ` timezone ${s.timezone}` : '';
        text = `Book slot ${s?.slot?.startTime ?? ''}${tz}`;
        lock({ bookingSlotSelected: true });
        break;
      }
      case 'reschedule-slot-selected': {
        // Reuses the booking-calendar SlotSelectedEvent; the agent's
        // confirm_reschedule_slot tool parses this exact "Reschedule slot ..."
        // shape (mirrors the booking "Book slot ..." contract).
        const s = p as { slot?: ChiliPiperSlot; timezone?: string };
        const tz = s?.timezone ? ` timezone ${s.timezone}` : '';
        text = `Reschedule slot ${s?.slot?.startTime ?? ''}${tz}`;
        lock({ rescheduleSlotSelected: true });
        break;
      }
      case 'call-objective-selected':
        // Load the option into the input so the user can edit before sending
        // (the cards say "edit if needed"); do NOT auto-send or lock. It locks
        // naturally once the user actually sends.
        this._setInputValue(String(p ?? ''));
        setTimeout(() => this.chatTextarea?.nativeElement?.focus(), 0);
        break;
      case 'guests-confirmed': {
        const guests = (p as AddedGuest[]) ?? [];
        // The wizard emits the FULL current list (it was pre-filled with prior
        // picks), so this is the authoritative running set — remember it to
        // pre-fill the next re-open of the picker.
        this._additionalGuests = guests;
        const emails = guests.map(g => g.email).filter(Boolean);
        text = emails.length ? `Additional guests: ${emails.join(', ')}` : 'none';
        break;
      }
      case 'cancel-confirmed': {
        const reason = (p as { reason?: string })?.reason ?? '';
        text = `I confirm cancellation. Reason: ${reason}`;
        lock({ cancelConfirmed: true });
        break;
      }
      case 'cancel-dismissed':
        text = "I changed my mind — don't cancel the meeting.";
        lock({ cancelDismissed: true });
        break;
      case 'participants-confirmed': {
        const emails = (p as string[]) ?? [];
        text = emails.length ? `Additional guests: ${emails.join(', ')}` : 'none';
        lock({ addParticipantsConfirmed: true });
        break;
      }
      case 'participants-cancelled':
        text = "I changed my mind — don't add any participants right now.";
        lock({ addParticipantsDismissed: true });
        break;
      case 'meeting-selected': {
        // Human-readable selection that ALSO carries the exact UTC start so the
        // agent can map it back to a cp_meeting_id in meetings_list (see prompt).
        const e = p as MeetingSelectedEvent;
        const m = e.meeting;
        text = `I select the meeting with ${m.executive} starting at ${e.displayTime} (UTC ${m.start_time}).`;
        lock({ meetingPicked: true });
        break;
      }
      case 'reschedule-done':
        text = "I've rescheduled.";
        lock({ rescheduleConfirmed: true });
        break;
      case 'booking-done':
        text = 'The meeting is booked — done.';
        break;
      case 'booking-failed':
        text = "The booking didn't go through.";
        break;
      case 'booking-retry-same':
        text = 'Show me the available times again.';
        break;
      case 'booking-retry-different':
        text = "I'd like to pick a different executive.";
        break;
      case 'post-booking-answered':
        if (String(p ?? '') === 'yes') {
          // Start a new booking for the same agent type that just completed.
          text = this.activeAgentId() === 'PCHECK_AGENT'
            ? "I'd like to book another Pulse Check meeting."
            : "I'd like to book another IMPACT Pulse Check meeting.";
        } else {
          // "No" — inject a local agent message; no backend round-trip needed.
          this.messages.update(msgs => [
            ...msgs,
            {
              id: ++this._msgId,
              role: 'agent' as const,
              content: "Your meeting has been booked! Is there anything else I can help you with?",
            },
          ]);
          return;
        }
        break;
    }

    if (!text) return;

    if (this.isInFlight()) {
      // The agent turn that rendered this widget hasn't emitted `done` yet —
      // e.g. the slot picker appeared (booking_slots event) while the backend is
      // still finishing the turn. Sending now would race a second turn onto the
      // session, so queue the message and flush it the instant `done` arrives.
      // The optimistic lock applied above keeps the widget in its pending state
      // (the booking calendar shows "confirming") until then, so the user's
      // selection is never silently dropped.
      this._pendingActionText = text;
      return;
    }
    this._sendMessage(sid, text);
  }

  /** Handle a user interaction from the AiRoleRenderer (e.g. quick-action chip). */
  onAiRoleAction(action: AiRoleAction): void {
    const sid = this.sessionId();
    if (!sid || action.type !== 'send-message') return;
    const text = action.payload;
    if (!text) return;
    if (this.isInFlight()) {
      this._pendingActionText = text;
      return;
    }
    this._sendMessage(sid, text);
  }

  /** Trigger the hidden file input. Works from welcome screen and active chat. */
  onUploadClick(): void {
    this.aiRoleFileInput?.nativeElement?.click();
  }

  /** Handle file selection: validate -> upload to S3 -> show attachment chip.
   *  Does NOT auto-send. The user writes their own message and sends when ready.
   *  The filename is appended to the outgoing message so the agent knows what
   *  was uploaded, regardless of which agent handles it. */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    // File type and size are checked by the upload endpoint, not here: it
    // returns a 400 with a user-facing message and logs every rejection
    // (upload_rejected), which a client-side check would hide. One round-trip
    // on a rejected file is the price of knowing how often limits are hit.

    // Use the active session ID or pre-generate one for welcome-screen uploads.
    let sid = this.sessionId();
    if (!sid) {
      if (!this._pendingUploadSessionId) {
        this._pendingUploadSessionId = generateSessionId();
      }
      sid = this._pendingUploadSessionId;
    }

    // Always call the active agent's own upload endpoint. The agent runtime
    // decides what to do with the file. Fall back to AI_ROLE_TRANSFORM on the
    // welcome screen (no active agent yet) since all agents share the same bucket.
    const agentId = this.activeAgentId() ?? 'AI_ROLE_TRANSFORM_AGENT';
    this.isUploading.set(true);

    this.aiRoleSvc.getUploadUrl(agentId, sid, file.name, file.size).subscribe({
      next: ({ upload_url }) => {
        this.aiRoleSvc.uploadFileToS3(upload_url, file).subscribe({
          next: () => {
            this.isUploading.set(false);
            this.pendingAttachment.set({ filename: file.name });
            // Note: we do NOT prompt for deal context here. The transcript is
            // just staged (chip shown). The deal-context step is triggered when
            // the user actually asks to analyze (onSendMessage), so it only ever
            // appears in the NEAT analyze flow — never the moment a file lands.
          },
          error: () => {
            this.isUploading.set(false);
            this.toast.error('Upload failed. Please try again.');
          },
        });
      },
      error: (err: { status?: number; error?: { message?: unknown } }) => {
        this.isUploading.set(false);
        // A 400 is the endpoint rejecting the file (type or size): show its
        // message as-is ("File is too large (412 KB). Maximum size is 300 KB.").
        const message = err?.status === 400 && typeof err.error?.message === 'string'
          ? err.error.message : '';
        if (message) {
          this.toast.warn(message, "Can't attach this file");
        } else {
          this.toast.error('Could not generate upload URL. Please try again.');
        }
      },
    });
  }

  /** Drop the staged attachment once an agent has actually consumed it (NEAT
   *  showed the deal-context wizard or produced a report; AI Role produced a
   *  download). Until then it persists so it survives a router "switch?" detour
   *  and is re-sent on the follow-up turn. */
  private _consumePendingAttachment(): void {
    if (this.pendingAttachment()) this.pendingAttachment.set(null);
  }

  clearAttachment(): void {
    this.pendingAttachment.set(null);
    // If we pre-generated a session for this upload and no session is active
    // yet, discard it so the next send gets a fresh one.
    if (!this.sessionId()) {
      this._pendingUploadSessionId = null;
    }
  }

  // ── NEAT deal-context flow ──────────────────────────────────────────────

  /**
   * The in-chat deal-context wizard (rendered from a `neat_ui` event) resolved.
   * `preamble` is the [Deal Context] block to send back ('' when the user
   * skipped). We send it as the next turn — NEAT is awaiting it and proceeds to
   * analyze the transcript it stashed. The wizard is locked (hidden) on submit.
   */
  onNeatDealContext(preamble: string, idx: number): void {
    const sid = this.sessionId();
    if (!sid) return;
    this._patchMsg(idx, { neatDealContextDone: true });
    const text = preamble || 'Skip deal context — analyze now.';
    if (this.isInFlight()) {
      // Turn 1 hasn't finished streaming yet — queue and flush on `done`
      // (same mechanism as pcheck actions).
      this._pendingActionText = text;
      return;
    }
    this._sendMessage(sid, text);
  }

  /**
   * Re-run a NEAT analysis on the same uploaded transcript, bypassing the
   * backend's per-transcript cache. Triggered by the "Reanalyze" button on a
   * NEAT report card. The runtime re-reads the original S3 file (by
   * upload_filename) and `force_reanalyze` skips the cache fast-path so a fresh
   * pass is produced and re-cached.
   */
  onReanalyze(artifact: Artifact): void {
    const filename = artifact.uploadFilename;
    const sid = this.sessionId();
    // Reanalyze only makes sense inside an existing session with a known source
    // file — the card hides the button otherwise (canReanalyze), this guards it.
    if (!filename || !sid) return;

    const text =
      `Reanalyze the uploaded transcript "${filename}" — run a fresh pass.\n\n[Attached file: ${filename}]`;
    const extra: Record<string, unknown> = {
      action: 'agenthome_chat',
      action_kind: 'chat',
      upload_filename: filename,
      force_reanalyze: true,
    };

    // Optimistic render: user bubble + thinking placeholder (mirrors _composeAndRender).
    this.messages.update(msgs => [...msgs, { id: ++this._msgId, role: 'user', content: text }]);
    this.isInFlight.set(true);
    this.messages.update(msgs => [...msgs, {
      id: ++this._msgId, role: 'agent', content: '', isThinking: true,
    }]);
    this._currentAgentMsgIdx = this.messages().length - 1;
    this._scrollToBottom(true);

    if (this.wsService.connectionState$.value === 'connected') {
      this._sendTurnFrame(sid, text, extra);
    } else {
      let sub: Subscription | undefined;
      sub = this.wsService.connectionState$.subscribe(state => {
        if (state === 'connected') {
          sub?.unsubscribe();
          this._sendTurnFrame(sid, text, extra);
        }
      });
    }
  }

  private _patchMsg(idx: number, patch: Partial<ChatMessage>): void {
    this.messages.update(msgs => {
      if (idx < 0 || idx >= msgs.length) return msgs;
      const copy = [...msgs];
      copy[idx] = { ...copy[idx], ...patch };
      return copy;
    });
  }

  private _markCurrentAgentMsgDone(): void {
    if (this._currentAgentMsgIdx < 0) return;
    this._updateAgentMsg(m => ({ ...m, isThinking: false }));
    this._currentAgentMsgIdx = -1;
  }

  /**
   * Resolve any tool pill still showing 'running' on the current message. Called
   * once the assistant starts emitting its answer: by then every tool that fed
   * that answer has finished, so a pill still "running" is one whose tool_result
   * either didn't arrive or didn't match by name (e.g. the supervisor's
   * sanitized `list_agents`). Without this it lingers as a grey spinner next to
   * the green ones until the end-of-turn done-flip. Guarded so it's a no-op
   * (and doesn't churn change detection) once nothing is running.
   */
  private _resolveStuckPills(): void {
    const idx = this._currentAgentMsgIdx;
    const m = idx >= 0 ? this.messages()[idx] : undefined;
    if (!m?.toolActivity?.some(a => a.status === 'running')) return;
    this._updateAgentMsg(mm => ({
      ...mm,
      toolActivity: mm.toolActivity?.map(a =>
        a.status === 'running' ? { ...a, status: 'done' as const } : a),
    }));
  }

  private _refreshSessions(): void {
    this.sessionListSvc.getSessions().subscribe({
      next: res => this.sessions.set(this._applyPendingRenames(res.sessions)),
      error: () => {},
    });
  }

  // ── Helpers ───────────────────────────────────────────────────

  relativeTime = relativeTime;

  /**
   * Scroll the thread to its end — but only when the user is already pinned to
   * the tail (within SCROLL_PIN_THRESHOLD_PX of the bottom). Pinned-ness is
   * read synchronously, BEFORE the just-updated messages render: measuring
   * after render would count the appended content against the threshold and
   * unpin a reader who was at the bottom. `force` bypasses the check for
   * user-initiated sends, where jumping to the new bubble is expected.
   */
  private _scrollToBottom(force = false): void {
    const el = this.msgContainer?.nativeElement;
    const pinned = !el
      || el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_PIN_THRESHOLD_PX;
    // The live Thinking block scrolls INTERNALLY once its body hits its
    // max-height, so following the stream also means pinning that inner
    // scroller. Same rule, same threshold: measure its pinned-ness before
    // render so appended reasoning doesn't unpin a reader at its tail, and
    // never yank a user who scrolled up inside the block.
    const body = this._liveReasoningBody();
    const bodyPinned = !body
      || body.scrollHeight - body.scrollTop - body.clientHeight <= SCROLL_PIN_THRESHOLD_PX;
    if (!force && !pinned && !bodyPinned) return;
    setTimeout(() => {
      if (force || bodyPinned) {
        const b = this._liveReasoningBody();
        if (b) b.scrollTop = b.scrollHeight;
      }
      if (force || pinned) {
        const node = this.msgContainer?.nativeElement;
        if (node) node.scrollTop = node.scrollHeight;
      }
    }, 0);
  }

  /** The most recent Thinking block's scrollable body, if one is rendered —
   *  the only one that can be receiving the live reasoning stream. */
  private _liveReasoningBody(): HTMLElement | null {
    const el = this.msgContainer?.nativeElement;
    if (!el) return null;
    const bodies = el.querySelectorAll<HTMLElement>('.ah-reasoning__body');
    return bodies.length ? bodies[bodies.length - 1] : null;
  }

}
